import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Users, DollarSign, BookOpen, Monitor, Download, CheckCircle, XCircle, Award, Upload, FileText, X, Search, Home, Utensils, Eye, EyeOff, Trash2, Settings, Bell, Save, Mail, Send, AlertCircle, Clock, Code, Github, Globe, UserPlus, Play, Star, Tag, Gift, Percent, Calendar, ToggleLeft, ToggleRight, Edit, Plus, LogOut, Shield, BarChart3, TrendingUp, PieChart, UserCog, ChevronLeft, ChevronRight, Menu, Share2, MessageSquare, Target, Gavel, Layers, UserCheck, Zap, Brain, Briefcase, Trophy, GraduationCap, RefreshCw, Loader, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminReferralsSection from '../components/AdminReferralsSection';
import AdminSubmissionsSection from '../components/AdminSubmissionsSection';
import AdminProblemStatementsSection from '../components/AdminProblemStatementsSection';
import AdminJuryManagementSection from '../components/AdminJuryManagementSection';
import AdminRoundConfigSection from '../components/AdminRoundConfigSection';
import AdminJuryAssignmentSection from '../components/AdminJuryAssignmentSection';
import AdminTeamSubmissionsSection from '../components/AdminTeamSubmissionsSection';
import AdminRoundFieldsManager from '../components/AdminRoundFieldsManager';
import SuperAdminConfigPanel from '../components/SuperAdminConfigPanel';
import SuperAdminPanel from '../components/admin/SuperAdminPanel';
import TeamRegistryAdmin from '../components/admin/TeamRegistryAdmin';
import POTDManagementAdmin from '../components/admin/POTDManagementAdmin';
import InvestorConnectAdmin from '../components/admin/InvestorConnectAdmin';
import AdminEventsSection from '../components/admin/AdminEventsSection';
import { useAdminSessionGuard } from '../hooks/useAdminSessionGuard';
import CompanyAssessmentAdmin from '../components/admin/CompanyAssessmentAdmin';
import CodeArenaStudentsAdmin from '../components/admin/CodeArenaStudentsAdmin';
import CodeArenaDashboardAdmin from '../components/admin/CodeArenaDashboardAdmin';
import CodeArenaContestAdmin from '../components/admin/CodeArenaContestAdmin';
import CodeArenaCertificatesAdmin from '../components/admin/CodeArenaCertificatesAdmin';
import FutureSkillsAdmin from '../components/admin/FutureSkillsAdmin';
import CollegesAdmin from '../components/admin/CollegesAdmin';
import HackathonParticipantManager from '../components/admin/HackathonParticipantManager';
import { getFrontendUrl } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to safely extract error message from API responses
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneSearchQuery, setPhoneSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all', 'completed', 'pending'
  const [memberCountFilter, setMemberCountFilter] = useState('all'); // 'all', '1-3', '4-6', '7-10'
  const [sortBy, setSortBy] = useState('registration_number'); // 'registration_number' or 'created_at'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // 10, 50, or 100
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('sales'); // 'sales', 'registrations', 'certificates', 'intents', 'bookings', 'submissions', 'communications', 'admin-team', or 'settings'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certMode, setCertMode] = useState('single'); // 'single' or 'bulk'
  const [showTeamDetailsModal, setShowTeamDetailsModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [intents, setIntents] = useState([]);
  const [intentSummary, setIntentSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all'); // 'all', 'accommodation_food', 'food_only'
  const [bookingPaymentFilter, setBookingPaymentFilter] = useState('all'); // 'all', 'completed', 'pending'
  // Sales Analytics State
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesDateRange, setSalesDateRange] = useState({ start: '', end: '' });
  const [salesServiceFilter, setSalesServiceFilter] = useState('all');
  // Admin Team State
  const [adminTeam, setAdminTeam] = useState([]);
  const [adminTeamLoading, setAdminTeamLoading] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ username: '', password: '', name: '', role: 'view_only' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  // Submission/Evaluation State
  const [submissions, setSubmissions] = useState([]);
  const [submissionStats, setSubmissionStats] = useState(null);
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState('all'); // 'all', 'pending', 'qualified', 'disqualified'
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [evaluationForm, setEvaluationForm] = useState({
    readme_exists: false,
    proper_structure: false,
    code_runs: false,
    tests_pass: false,
    on_time: true
  });
  const [evaluating, setEvaluating] = useState(false);
  // Jury Management State
  const [juryList, setJuryList] = useState([]);
  const [showJuryModal, setShowJuryModal] = useState(false);
  const [juryForm, setJuryForm] = useState({ username: '', password: '', name: '' });
  const [creatingJury, setCreatingJury] = useState(false);
  const [assigningJury, setAssigningJury] = useState(false);
  // Notification Banner Settings
  const [notificationBanner, setNotificationBanner] = useState({ text: '', is_active: true });
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerSaved, setBannerSaved] = useState(false);
  // Edition-Based Notification Bar Settings
  const [editionBars, setEditionBars] = useState({
    hackathon: {
      enabled: true,
      text: '🚀 EvalentX 2nd Edition registrations are now open!',
      redirect_link: '/register',
      background_color: '#7c3aed',
      expiry_date: ''
    },
    ai_league: {
      enabled: true,
      text: '🧠 Join India\'s AI Premium League - ₹5,00,000 Prize Pool!',
      redirect_link: '/ai-league',
      background_color: '#0891b2',
      expiry_date: ''
    }
  });
  const [editionBarLoading, setEditionBarLoading] = useState({ hackathon: false, ai_league: false });
  const [editionBarSaved, setEditionBarSaved] = useState({ hackathon: false, ai_league: false });
  // Submission Visibility Setting
  const [submissionEnabled, setSubmissionEnabled] = useState(false);
  const [submissionEnabledLoading, setSubmissionEnabledLoading] = useState(false);
  // Pricing Management State
  const [pricing, setPricing] = useState({
    hackathon_entry_fee: 1200,
    accommodation_with_food: 2500,
    accommodation_only: 1500,
    food_only: 1000
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(false);
  const [applyingPricing, setApplyingPricing] = useState(false);
  // Coupon & Referral State
  const [coupons, setCoupons] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    applicable_to: ['hackathon_entry_fee'],
    max_uses: null,
    max_uses_per_user: 1,
    valid_from: '',
    valid_until: '',
    is_active: true,
    description: ''
  });
  const [savingCoupon, setSavingCoupon] = useState(false);
  // Date-Based Pricing State
  const [datePricing, setDatePricing] = useState({
    accommodation_food: { '1_day': 1000, '2_days': 1800, '3_days': 2500 },
    accommodation_only: { '1_day': 600, '2_days': 1100, '3_days': 1500 },
    food_only: { '1_day': 400, '2_days': 700, '3_days': 1000 }
  });
  const [datePricingLoading, setDatePricingLoading] = useState(false);
  const [datePricingSaved, setDatePricingSaved] = useState(false);
  // Resume Pricing State
  const [resumePricing, setResumePricing] = useState({
    premium_price: 99,
    ai_optimization_price: 49,
    currency: 'INR'
  });
  const [resumePricingLoading, setResumePricingLoading] = useState(false);
  const [resumePricingSaved, setResumePricingSaved] = useState(false);
  // Registration Control State
  const [registrationSettings, setRegistrationSettings] = useState({
    hackathon_registration: true,
    accommodation_booking: true,
    food_booking: true
  });
  const [registrationSettingsLoading, setRegistrationSettingsLoading] = useState(false);
  // Resume Upgrades State
  const [resumeUpgrades, setResumeUpgrades] = useState({ orders: [], summary: {} });
  const [resumeUpgradesLoading, setResumeUpgradesLoading] = useState(false);
  const [resumeUpgradeFilter, setResumeUpgradeFilter] = useState('all');
  const [resumeUpgradePage, setResumeUpgradePage] = useState(1);
  // Job Approvals State
  const [pendingJobs, setPendingJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobStatusFilter, setJobStatusFilter] = useState('pending');
  // Enterprise Hosting State
  const [enterpriseChallenges, setEnterpriseChallenges] = useState([]);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);
  const [enterprisePricing, setEnterprisePricing] = useState({});
  const [enterpriseAnalytics, setEnterpriseAnalytics] = useState(null);
  const [enterpriseFilter, setEnterpriseFilter] = useState('pending');
  const [showEnterprisePricingModal, setShowEnterprisePricingModal] = useState(false);
  const [editingEnterprisePlan, setEditingEnterprisePlan] = useState(null);
  const [enterprisePlanForm, setEnterprisePlanForm] = useState({
    name: '', price: 0, features: [], max_participants: 1000, max_rounds: 3, enabled: true
  });
  const [enterpriseCoupons, setEnterpriseCoupons] = useState([]);
  const [showEnterpriseCouponModal, setShowEnterpriseCouponModal] = useState(false);
  const [editingEnterpriseCoupon, setEditingEnterpriseCoupon] = useState(null);
  const [enterpriseCouponForm, setEnterpriseCouponForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10, applicable_plans: ['starter', 'pro', 'enterprise'],
    max_uses: 100, used_count: 0, valid_from: '', valid_until: '', is_active: true
  });
  const [enterpriseActiveSubTab, setEnterpriseActiveSubTab] = useState('challenges');
  // Enterprise Status Update State
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({ challengeId: '', challengeTitle: '', currentStatus: '', newStatus: '', reason: '' });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // Session Security State
  const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  // Email Communication State
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailRecipientType, setEmailRecipientType] = useState('all'); // 'all', 'paid', 'unpaid', 'selected'
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showEmailResultModal, setShowEmailResultModal] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const navigate = useNavigate();
  
  // Certificate form state
  const [certForm, setCertForm] = useState({
    participant_name: '',
    participant_email: '',
    workshop_name: '',
    team_name: '',
    event_dates: '6th - 8th February 2026',
    event_location: 'IIT Gandhinagar, Gujarat',
    issue_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  });
  
  const [certLogos, setCertLogos] = useState({
    partner_logos: [],
    codeversity_logo: null,
    ceo_signature: null,
    mentor_signature: null
  });
  
  const [bulkCsvFile, setBulkCsvFile] = useState(null);

  // Interview Credits Management State
  const [creditStudents, setCreditStudents] = useState([]);
  const [creditSearch, setCreditSearch] = useState('');
  const [creditSearchLoading, setCreditSearchLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState(5);
  const [creditReason, setCreditReason] = useState('Admin granted');
  const [grantingCredit, setGrantingCredit] = useState(null);
  const [creditLogs, setCreditLogs] = useState([]);
  const [creditLogsLoading, setCreditLogsLoading] = useState(false);

  useAdminSessionGuard((msg) => {
    setIsLoggedIn(false);
    setLoginError(msg);
    toast.error(msg);
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsLoggedIn(true);
      fetchRegistrations();
      fetchSalesAnalytics();
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [registrations, searchQuery, phoneSearchQuery, paymentStatusFilter, memberCountFilter]);

  // Fetch data when tab changes
  useEffect(() => {
    if (isLoggedIn) {
      if (activeTab === 'sales') fetchSalesAnalytics();
      if (activeTab === 'admin-team') fetchAdminTeam();
    }
  }, [activeTab, isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      localStorage.setItem('admin_token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      setIsLoggedIn(true);
      fetchRegistrations();
      fetchSalesAnalytics();
    } catch (err) {
      setLoginError(getErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/admin/registrations`);
      setRegistrations(response.data.teams);
      setFilteredRegistrations(response.data.teams);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to fetch registrations:', err);
    }
  };

  // Sales Analytics Functions
  const fetchSalesAnalytics = async () => {
    setSalesLoading(true);
    try {
      let url = `${API}/admin/sales-analytics`;
      const params = new URLSearchParams();
      if (salesDateRange.start) params.append('start_date', salesDateRange.start);
      if (salesDateRange.end) params.append('end_date', salesDateRange.end);
      if (salesServiceFilter !== 'all') params.append('service_type', salesServiceFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setSalesData(response.data);
    } catch (err) {
      console.error('Failed to fetch sales analytics:', err);
      toast.error('Failed to load sales data');
    } finally {
      setSalesLoading(false);
    }
  };

  // Admin Team Functions
  const fetchAdminTeam = async () => {
    setAdminTeamLoading(true);
    try {
      const response = await axios.get(`${API}/admin/team`);
      setAdminTeam(response.data.admins);
    } catch (err) {
      console.error('Failed to fetch admin team:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view admin team');
      }
    } finally {
      setAdminTeamLoading(false);
    }
  };

  const createAdminUser = async () => {
    if (!newAdminForm.username || !newAdminForm.password || !newAdminForm.name) {
      toast.error('Please fill all required fields');
      return;
    }
    if (newAdminForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setCreatingAdmin(true);
    try {
      await axios.post(`${API}/admin/team`, newAdminForm);
      toast.success('Admin user created successfully');
      setShowAddAdminModal(false);
      setNewAdminForm({ username: '', password: '', name: '', role: 'view_only' });
      fetchAdminTeam();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create admin user'));
    } finally {
      setCreatingAdmin(false);
    }
  };

  const toggleAdminStatus = async (username, currentStatus) => {
    try {
      await axios.put(`${API}/admin/team/${username}`, { is_active: !currentStatus });
      toast.success(`Admin user ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchAdminTeam();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update admin status'));
    }
  };

  const deleteAdminUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete admin user "${username}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/team/${username}`);
      toast.success('Admin user deleted');
      fetchAdminTeam();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete admin user'));
    }
  };

  const applyFilters = () => {
    let filtered = [...registrations];

    // Filter by email search
    if (searchQuery.trim()) {
      filtered = filtered.filter((team) => {
        const leader = team.members?.find((member, index) => index === 0);
        const leaderEmail = leader?.email?.toLowerCase() || '';
        return leaderEmail.includes(searchQuery.toLowerCase());
      });
    }

    // Filter by phone search
    if (phoneSearchQuery.trim()) {
      filtered = filtered.filter((team) => {
        const leader = team.members?.find((member, index) => index === 0);
        const leaderPhone = leader?.phone || '';
        return leaderPhone.includes(phoneSearchQuery.trim());
      });
    }

    // Filter by payment status
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter((team) => {
        return team.payment_status === paymentStatusFilter;
      });
    }

    // Filter by member count
    if (memberCountFilter !== 'all') {
      filtered = filtered.filter((team) => {
        const memberCount = team.total_members || 0;
        switch (memberCountFilter) {
          case '1-3':
            return memberCount >= 1 && memberCount <= 3;
          case '4-6':
            return memberCount >= 4 && memberCount <= 6;
          case '7-10':
            return memberCount >= 7 && memberCount <= 10;
          default:
            return true;
        }
      });
    }

    setFilteredRegistrations(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handlePhoneSearch = (query) => {
    setPhoneSearchQuery(query);
  };

  const handlePaymentFilterChange = (status) => {
    setPaymentStatusFilter(status);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedRegistrations = () => {
    const sorted = [...filteredRegistrations].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'created_at') {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        comparison = dateA - dateB;
      } else if (sortBy === 'registration_number') {
        comparison = a.registration_number - b.registration_number;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  };

  const getPaginatedRegistrations = () => {
    const sorted = getSortedRegistrations();
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredRegistrations.length / recordsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRecordsPerPageChange = (records) => {
    setRecordsPerPage(records);
    setCurrentPage(1); // Reset to first page
  };

  const handleViewTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetailsModal(true);
  };

  const closeTeamDetailsModal = () => {
    setShowTeamDetailsModal(false);
    setSelectedTeam(null);
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificates`);
      setCertificates(response.data.certificates);
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
      toast.error('Failed to load certificates');
    }
  };

  const fetchIntents = async () => {
    try {
      const response = await axios.get(`${API}/admin/intents`);
      setIntents(response.data.teams);
      setIntentSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to fetch intents:', err);
      toast.error('Failed to load intent data');
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`);
      setBookings(response.data.bookings);
      setBookingSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      toast.error('Failed to load booking data');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking status updated to ${status}`);
      fetchBookings();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update booking status'));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(`Are you sure you want to delete booking ${bookingId}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success('Booking deleted successfully');
      fetchBookings();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete booking'));
    }
  };

  const handleViewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetailsModal(true);
  };

  // Export bookings to CSV with member details
  const handleExportBookings = async (format = 'csv') => {
    try {
      toast.info('Preparing export...');
      
      const response = await axios.get(`${API}/admin/bookings/export`, {
        params: {
          booking_type: bookingTypeFilter,
          payment_status: bookingPaymentFilter
        }
      });
      
      const { bookings: exportData, filters_applied } = response.data;
      
      if (exportData.length === 0) {
        toast.warning('No bookings to export with current filters');
        return;
      }
      
      // Create CSV content
      const csvRows = [];
      
      // Header row
      const headers = [
        'Booking ID',
        'Team Name',
        'Team ID',
        'College',
        'Team Leader Name',
        'Team Leader Email',
        'Team Leader Phone',
        'Booking Type',
        'Number of Members',
        'Payment Status',
        'Total Amount (₹)',
        'Validity Days',
        'Booking Date',
        'Member 1 Name', 'Member 1 Email', 'Member 1 Phone', 'Member 1 College', 'Member 1 Participant ID',
        'Member 2 Name', 'Member 2 Email', 'Member 2 Phone', 'Member 2 College', 'Member 2 Participant ID',
        'Member 3 Name', 'Member 3 Email', 'Member 3 Phone', 'Member 3 College', 'Member 3 Participant ID',
        'Member 4 Name', 'Member 4 Email', 'Member 4 Phone', 'Member 4 College', 'Member 4 Participant ID',
        'Member 5 Name', 'Member 5 Email', 'Member 5 Phone', 'Member 5 College', 'Member 5 Participant ID',
        'Member 6 Name', 'Member 6 Email', 'Member 6 Phone', 'Member 6 College', 'Member 6 Participant ID',
        'Member 7 Name', 'Member 7 Email', 'Member 7 Phone', 'Member 7 College', 'Member 7 Participant ID',
        'Member 8 Name', 'Member 8 Email', 'Member 8 Phone', 'Member 8 College', 'Member 8 Participant ID',
        'Member 9 Name', 'Member 9 Email', 'Member 9 Phone', 'Member 9 College', 'Member 9 Participant ID',
        'Member 10 Name', 'Member 10 Email', 'Member 10 Phone', 'Member 10 College', 'Member 10 Participant ID'
      ];
      csvRows.push(headers.join(','));
      
      // Data rows
      for (const booking of exportData) {
        const members = booking.members || [];
        const row = [
          `"${booking.booking_id}"`,
          `"${(booking.team_name || '').replace(/"/g, '""')}"`,
          `"${booking.team_id}"`,
          `"${(booking.college || '').replace(/"/g, '""')}"`,
          `"${(booking.leader_name || '').replace(/"/g, '""')}"`,
          `"${booking.leader_email}"`,
          `"${booking.leader_phone}"`,
          `"${booking.booking_type}"`,
          booking.member_count,
          `"${booking.payment_status}"`,
          booking.total_amount,
          booking.validity_days,
          `"${booking.created_at ? new Date(booking.created_at).toLocaleDateString('en-IN') : 'N/A'}"`
        ];
        
        // Add member details (up to 10 members)
        for (let i = 0; i < 10; i++) {
          const member = members[i] || {};
          row.push(
            `"${(member.name || '').replace(/"/g, '""')}"`,
            `"${member.email || ''}"`,
            `"${member.phone || ''}"`,
            `"${(member.college || '').replace(/"/g, '""')}"`,
            `"${member.participant_id || ''}"`
          );
        }
        
        csvRows.push(row.join(','));
      }
      
      const csvContent = csvRows.join('\n');
      
      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with filters
      const filterSuffix = filters_applied.booking_type !== 'all' ? `_${filters_applied.booking_type}` : '';
      const statusSuffix = filters_applied.payment_status !== 'all' ? `_${filters_applied.payment_status}` : '';
      const filename = `bookings_export${filterSuffix}${statusSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${exportData.length} bookings successfully!`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(getErrorMessage(err, 'Failed to export bookings'));
    }
  };

  const handleDeleteCertificate = async (certificateId, certificateNumber) => {
    if (!window.confirm(`Are you sure you want to delete certificate ${certificateNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/certificates/${certificateId}`);
      toast.success('Certificate deleted successfully');
      fetchCertificates();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete certificate'));
    }
  };

  const handleGenerateSingleCertificate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new FormData();
      Object.keys(certForm).forEach(key => {
        formData.append(key, certForm[key]);
      });
      
      // Add multiple partner logos
      if (certLogos.partner_logos && certLogos.partner_logos.length > 0) {
        certLogos.partner_logos.forEach((logo) => {
          formData.append('partner_logos', logo);
        });
      }
      
      if (certLogos.codeversity_logo) formData.append('codeversity_logo', certLogos.codeversity_logo);
      if (certLogos.ceo_signature) formData.append('ceo_signature', certLogos.ceo_signature);
      if (certLogos.mentor_signature) formData.append('mentor_signature', certLogos.mentor_signature);
      
      const response = await axios.post(`${API}/admin/generate-certificate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Certificate generated successfully!');
      setShowCertModal(false);
      fetchCertificates();
      resetCertForm();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate certificate'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBulkCertificates = async (e) => {
    e.preventDefault();
    if (!bulkCsvFile) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('csv_file', bulkCsvFile);
      
      // Add multiple partner logos
      if (certLogos.partner_logos && certLogos.partner_logos.length > 0) {
        certLogos.partner_logos.forEach((logo) => {
          formData.append('partner_logos', logo);
        });
      }
      
      if (certLogos.codeversity_logo) formData.append('codeversity_logo', certLogos.codeversity_logo);
      if (certLogos.ceo_signature) formData.append('ceo_signature', certLogos.ceo_signature);
      if (certLogos.mentor_signature) formData.append('mentor_signature', certLogos.mentor_signature);
      
      const response = await axios.post(`${API}/admin/generate-certificates-bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Generated ${response.data.certificates.length} certificates!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setShowCertModal(false);
      fetchCertificates();
      resetCertForm();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate certificates'));
    } finally {
      setLoading(false);
    }
  };

  const resetCertForm = () => {
    setCertForm({
      participant_name: '',
      participant_email: '',
      workshop_name: '',
      team_name: '',
      event_dates: '6th - 8th February 2026',
      event_location: 'IIT Gandhinagar, Gujarat',
      issue_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    });
    setCertLogos({
      partner_logos: [],
      codeversity_logo: null,
      ceo_signature: null,
      mentor_signature: null
    });
    setBulkCsvFile(null);
  };

  useEffect(() => {
    if (activeTab === 'certificates' && isLoggedIn) {
      fetchCertificates();
    } else if (activeTab === 'intents' && isLoggedIn) {
      fetchIntents();
    } else if (activeTab === 'bookings' && isLoggedIn) {
      fetchBookings();
    } else if (activeTab === 'submissions' && isLoggedIn) {
      fetchSubmissions();
      fetchJuryList();
    } else if (activeTab === 'settings' && isLoggedIn) {
      fetchNotificationBanner();
      fetchEditionBars();
      fetchSubmissionEnabled();
      fetchPricing();
      fetchCoupons();
      fetchReferrals();
      fetchDatePricing();
      fetchResumePricing();
      fetchRegistrationSettings();
    } else if (activeTab === 'communications' && isLoggedIn) {
      fetchEmailLogs();
      if (!registrations || registrations.length === 0) {
        fetchRegistrations();
      }
    } else if (activeTab === 'resume-upgrades' && isLoggedIn) {
      fetchResumeUpgrades();
    } else if (activeTab === 'job-approvals' && isLoggedIn) {
      fetchJobApprovals();
    } else if (activeTab === 'enterprise-hosting' && isLoggedIn) {
      fetchEnterpriseData();
    } else if (activeTab === 'interview-credits' && isLoggedIn) {
      fetchCreditLogs();
    }
  }, [activeTab, isLoggedIn]);

  // Job Approval Functions - uses upgraded /admin/super/jobs/* endpoints (both collections, soft delete)
  const fetchJobApprovals = async (status = jobStatusFilter) => {
    setJobsLoading(true);
    try {
      const s = status || 'all';
      const response = await axios.get(`${API}/admin/super/jobs?status=${s}&page=1&limit=100`);
      const jobs = response.data.jobs || [];
      if (s === 'pending') setPendingJobs(jobs);
      setAllJobs(jobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      toast.error('Failed to fetch job postings');
    } finally {
      setJobsLoading(false);
    }
  };

  const handleApproveJob = async (jobId) => {
    try {
      await axios.put(`${API}/admin/super/jobs/${jobId}/approve`);
      toast.success('Job approved successfully');
      fetchJobApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve job');
    }
  };

  const handleRejectJob = async (jobId) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try {
      await axios.put(`${API}/admin/super/jobs/${jobId}/reject`, { reason });
      toast.success('Job rejected');
      fetchJobApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Soft-delete this job? It can be restored later from the "Deleted" filter.')) return;
    try {
      await axios.delete(`${API}/admin/super/jobs/${jobId}`);
      toast.success('Job deleted (can be restored)');
      fetchJobApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  const handleRestoreJob = async (jobId) => {
    try {
      const res = await axios.put(`${API}/admin/super/jobs/${jobId}/restore`);
      toast.success(res.data?.message || 'Job restored');
      fetchJobApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore job');
    }
  };

  const handlePurgeJob = async (jobId) => {
    if (!window.confirm('Permanently purge this job? This CANNOT be undone.')) return;
    try {
      await axios.delete(`${API}/admin/super/jobs/${jobId}/purge`);
      toast.success('Job permanently purged');
      fetchJobApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge job');
    }
  };

  // Enterprise Hosting Functions
  const fetchEnterpriseData = async (status = enterpriseFilter) => {
    setEnterpriseLoading(true);
    try {
      const [challengesRes, pricingRes, analyticsRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/enterprise/challenges${status !== 'all' ? `?status=${status}` : ''}`),
        axios.get(`${API}/admin/enterprise/pricing`),
        axios.get(`${API}/admin/enterprise/analytics`),
        axios.get(`${API}/admin/enterprise/coupons`).catch(() => ({ data: { coupons: [] } }))
      ]);
      setEnterpriseChallenges(challengesRes.data.challenges || []);
      setEnterprisePricing(pricingRes.data.plans || {});
      setEnterpriseAnalytics(analyticsRes.data);
      setEnterpriseCoupons(couponsRes.data.coupons || []);
    } catch (err) {
      console.error('Failed to fetch enterprise data:', err);
    } finally {
      setEnterpriseLoading(false);
    }
  };

  const handleApproveEnterprise = async (challengeId) => {
    try {
      await axios.post(`${API}/admin/enterprise/challenges/${challengeId}/approve`);
      toast.success('Challenge approved');
      fetchEnterpriseData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectEnterprise = async (challengeId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      await axios.post(`${API}/admin/enterprise/challenges/${challengeId}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('Challenge rejected');
      fetchEnterpriseData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  // Enterprise Status Update with Confirmation
  const ENTERPRISE_STATUSES = [
    { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    { value: 'pending_approval', label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
    { value: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-700' },
    { value: 'live', label: 'Live', color: 'bg-green-100 text-green-700' },
    { value: 'expired', label: 'Expired', color: 'bg-gray-100 text-gray-600' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' }
  ];

  const isCriticalStatusChange = (newStatus) => ['live', 'expired', 'cancelled', 'rejected'].includes(newStatus);

  const initiateStatusChange = (challenge, newStatus) => {
    if (isCriticalStatusChange(newStatus)) {
      setStatusChangeData({
        challengeId: challenge.id,
        challengeTitle: challenge.title,
        currentStatus: challenge.status,
        newStatus,
        reason: ''
      });
      setShowStatusConfirmModal(true);
    } else {
      handleUpdateEnterpriseStatus(challenge.id, newStatus);
    }
  };

  const handleUpdateEnterpriseStatus = async (challengeId, newStatus, reason = '') => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/admin/enterprise/challenges/${challengeId}/status`, {
        status: newStatus,
        reason: reason || null
      });
      toast.success(`Challenge status updated to "${newStatus}"`);
      setShowStatusConfirmModal(false);
      setStatusChangeData({ challengeId: '', challengeTitle: '', currentStatus: '', newStatus: '', reason: '' });
      fetchEnterpriseData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const copyEnterpriseLink = (challenge, linkType = 'landing') => {
    const baseUrl = getFrontendUrl();
    let url = '';
    const ref = '?ref=admin_dashboard';
    
    if (linkType === 'landing') {
      url = `${baseUrl}/enterprise/${challenge.org_slug}/${challenge.slug}${ref}`;
    } else if (linkType === 'assessment') {
      url = `${baseUrl}/enterprise/${challenge.id}/assessment${ref}`;
    }
    
    navigator.clipboard.writeText(url);
    toast.success(`${linkType === 'landing' ? 'Challenge' : 'Assessment'} link copied!`);
  };

  const handleUpdateEnterprisePricing = async (plans) => {
    try {
      await axios.put(`${API}/admin/enterprise/pricing`, plans);
      toast.success('Pricing updated successfully');
      fetchEnterpriseData();
    } catch (err) {
      toast.error('Failed to update pricing');
    }
  };

  const handleSaveEnterprisePlan = async () => {
    if (!editingEnterprisePlan) return;
    
    try {
      const updatedPlans = {
        ...enterprisePricing,
        [editingEnterprisePlan]: {
          ...enterprisePricing[editingEnterprisePlan],
          ...enterprisePlanForm,
          id: editingEnterprisePlan
        }
      };
      await axios.put(`${API}/admin/enterprise/pricing`, updatedPlans);
      toast.success('Plan updated successfully');
      setShowEnterprisePricingModal(false);
      setEditingEnterprisePlan(null);
      fetchEnterpriseData();
    } catch (err) {
      toast.error('Failed to update plan');
    }
  };

  const openEditEnterprisePlan = (planId) => {
    const plan = enterprisePricing[planId];
    if (plan) {
      setEditingEnterprisePlan(planId);
      setEnterprisePlanForm({
        name: plan.name || '',
        price: plan.price || 0,
        features: plan.features || [],
        max_participants: plan.max_participants || 1000,
        max_rounds: plan.max_rounds || 3,
        enabled: plan.enabled !== false
      });
      setShowEnterprisePricingModal(true);
    }
  };

  const handleSaveEnterpriseCoupon = async () => {
    if (!enterpriseCouponForm.code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    try {
      if (editingEnterpriseCoupon) {
        await axios.put(`${API}/admin/enterprise/coupons/${editingEnterpriseCoupon.id}`, enterpriseCouponForm);
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/admin/enterprise/coupons`, enterpriseCouponForm);
        toast.success('Coupon created');
      }
      setShowEnterpriseCouponModal(false);
      setEditingEnterpriseCoupon(null);
      setEnterpriseCouponForm({
        code: '', discount_type: 'percentage', discount_value: 10, applicable_plans: ['starter', 'pro', 'enterprise'],
        max_uses: 100, used_count: 0, valid_from: '', valid_until: '', is_active: true
      });
      fetchEnterpriseData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const openEditEnterpriseCoupon = (coupon) => {
    setEditingEnterpriseCoupon(coupon);
    setEnterpriseCouponForm({
      code: coupon.code || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || 10,
      applicable_plans: coupon.applicable_plans || ['starter', 'pro', 'enterprise'],
      max_uses: coupon.max_uses || 100,
      used_count: coupon.used_count || 0,
      valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      is_active: coupon.is_active !== false
    });
    setShowEnterpriseCouponModal(true);
  };

  const toggleEnterpriseCouponStatus = async (couponId) => {
    try {
      await axios.put(`${API}/admin/enterprise/coupons/${couponId}/toggle`);
      toast.success('Coupon status updated');
      fetchEnterpriseData();
    } catch (err) {
      toast.error('Failed to toggle coupon');
    }
  };

  const deleteEnterpriseCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await axios.delete(`${API}/admin/enterprise/coupons/${couponId}`);
      toast.success('Coupon deleted');
      fetchEnterpriseData();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  // Submission/Evaluation Functions
  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`);
      setSubmissions(response.data.submissions || []);
      setSubmissionStats(response.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to fetch submissions');
    }
  };

  const fetchJuryList = async () => {
    try {
      const response = await axios.get(`${API}/admin/jury`);
      setJuryList(response.data.jury || []);
    } catch (err) {
      console.error('Failed to fetch jury list:', err);
    }
  };

  const handleEvaluateSubmission = async () => {
    if (!selectedSubmission) return;
    setEvaluating(true);
    try {
      await axios.post(`${API}/admin/submissions/${selectedSubmission.id}/evaluate`, evaluationForm);
      toast.success('Submission evaluated successfully');
      setShowEvaluationModal(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to evaluate submission'));
    } finally {
      setEvaluating(false);
    }
  };

  const handleManualStatusUpdate = async (submissionId, status) => {
    try {
      await axios.post(`${API}/admin/submissions/${submissionId}/manual-status?status=${status}`);
      toast.success(`Submission marked as ${status}`);
      fetchSubmissions();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    }
  };

  const handleCreateJury = async (e) => {
    e.preventDefault();
    if (!juryForm.username || !juryForm.password || !juryForm.name) {
      toast.error('Please fill in all fields');
      return;
    }
    setCreatingJury(true);
    try {
      const formData = new FormData();
      formData.append('username', juryForm.username);
      formData.append('password', juryForm.password);
      formData.append('name', juryForm.name);
      
      await axios.post(`${API}/admin/jury/create`, formData);
      toast.success(`Jury account created for ${juryForm.name}`);
      setJuryForm({ username: '', password: '', name: '' });
      setShowJuryModal(false);
      fetchJuryList();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create jury account'));
    } finally {
      setCreatingJury(false);
    }
  };

  const handleAutoAssignJury = async () => {
    setAssigningJury(true);
    try {
      const response = await axios.post(`${API}/admin/jury/assign`);
      toast.success(response.data.message);
      fetchSubmissions();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to assign jury'));
    } finally {
      setAssigningJury(false);
    }
  };

  const getFilteredSubmissions = () => {
    if (submissionStatusFilter === 'all') return submissions;
    return submissions.filter(s => s.status === submissionStatusFilter);
  };

  const fetchNotificationBanner = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/notification-banner`);
      setNotificationBanner(response.data);
    } catch (err) {
      console.error('Failed to fetch notification banner:', err);
    }
  };

  const updateNotificationBanner = async () => {
    if (!notificationBanner.text || notificationBanner.text.trim().length < 5) {
      toast.error('Banner text must be at least 5 characters');
      return;
    }
    
    setBannerLoading(true);
    try {
      await axios.put(`${API}/admin/settings/notification-banner`, {
        text: notificationBanner.text,
        is_active: notificationBanner.is_active
      });
      toast.success('Notification banner updated successfully!');
      setBannerSaved(true);
      setTimeout(() => setBannerSaved(false), 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update notification banner'));
    } finally {
      setBannerLoading(false);
    }
  };

  // Edition-Based Notification Bar Functions
  const fetchEditionBars = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/edition-notification-bars`);
      setEditionBars(response.data);
    } catch (err) {
      console.error('Failed to fetch edition notification bars:', err);
    }
  };

  const updateEditionBar = async (edition) => {
    const bar = editionBars[edition];
    if (bar.text && bar.text.trim().length < 5) {
      toast.error('Notification text must be at least 5 characters');
      return;
    }
    
    setEditionBarLoading(prev => ({ ...prev, [edition]: true }));
    try {
      await axios.put(`${API}/admin/settings/edition-notification-bar`, {
        edition,
        enabled: bar.enabled,
        text: bar.text,
        redirect_link: bar.redirect_link || null,
        background_color: bar.background_color || null,
        expiry_date: bar.expiry_date || null
      });
      toast.success(`${edition === 'hackathon' ? 'Hackathon' : 'AI League'} notification bar updated!`);
      setEditionBarSaved(prev => ({ ...prev, [edition]: true }));
      setTimeout(() => setEditionBarSaved(prev => ({ ...prev, [edition]: false })), 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update notification bar'));
    } finally {
      setEditionBarLoading(prev => ({ ...prev, [edition]: false }));
    }
  };

  const updateEditionBarField = (edition, field, value) => {
    setEditionBars(prev => ({
      ...prev,
      [edition]: {
        ...prev[edition],
        [field]: value
      }
    }));
  };

  // Submission Visibility Functions
  const fetchSubmissionEnabled = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/submission-enabled`);
      setSubmissionEnabled(response.data.enabled || false);
    } catch (err) {
      console.error('Failed to fetch submission setting:', err);
    }
  };

  const toggleSubmissionEnabled = async () => {
    setSubmissionEnabledLoading(true);
    try {
      const response = await axios.put(`${API}/admin/settings/submission-enabled?enabled=${!submissionEnabled}`);
      setSubmissionEnabled(!submissionEnabled);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update submission setting'));
    } finally {
      setSubmissionEnabledLoading(false);
    }
  };

  // Pricing Management Functions
  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/pricing`);
      if (response.data.prices) {
        setPricing(response.data.prices);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const updatePricing = async () => {
    setPricingLoading(true);
    try {
      await axios.put(`${API}/admin/settings/pricing`, pricing);
      toast.success('Pricing updated successfully!');
      setPricingSaved(true);
      setTimeout(() => setPricingSaved(false), 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update pricing'));
    } finally {
      setPricingLoading(false);
    }
  };

  const applyPricingToUnpaid = async () => {
    setApplyingPricing(true);
    try {
      const response = await axios.post(`${API}/admin/settings/pricing/apply-to-unpaid`);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to apply pricing to unpaid teams'));
    } finally {
      setApplyingPricing(false);
    }
  };

  // Coupon & Referral Functions
  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data.coupons || []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/admin/referrals`);
      setReferrals(response.data.referrals || []);
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
    }
  };

  const fetchDatePricing = async () => {
    try {
      const response = await axios.get(`${API}/bookings/date-pricing`);
      if (response.data.pricing) {
        setDatePricing(response.data.pricing);
      }
    } catch (err) {
      console.error('Failed to fetch date pricing:', err);
    }
  };

  const fetchResumePricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/resume-pricing`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.data) {
        setResumePricing({
          premium_price: response.data.premium_price || 99,
          ai_optimization_price: response.data.ai_optimization_price || 49,
          currency: response.data.currency || 'INR'
        });
      }
    } catch (err) {
      console.error('Failed to fetch resume pricing:', err);
    }
  };

  const updateResumePricing = async () => {
    setResumePricingLoading(true);
    try {
      await axios.put(`${API}/admin/settings/resume-pricing`, resumePricing, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Resume pricing updated successfully!');
      setResumePricingSaved(true);
      setTimeout(() => setResumePricingSaved(false), 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update resume pricing'));
    } finally {
      setResumePricingLoading(false);
    }
  };

  const fetchResumeUpgrades = async (status = resumeUpgradeFilter, page = resumeUpgradePage) => {
    setResumeUpgradesLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.append('status', status);
      params.append('page', page);
      params.append('limit', 15);
      
      const response = await axios.get(`${API}/admin/resume-upgrades?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setResumeUpgrades(response.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to fetch resume upgrades'));
    } finally {
      setResumeUpgradesLoading(false);
    }
  };

  const updateResumeUpgradeStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/resume-upgrades/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }}
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchResumeUpgrades();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    }
  };

  const exportResumeUpgrades = async () => {
    try {
      const params = resumeUpgradeFilter !== 'all' ? `?status=${resumeUpgradeFilter}` : '';
      const response = await axios.get(`${API}/admin/resume-upgrades/export${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resume_upgrades.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export downloaded!');
    } catch (err) {
      toast.error('Failed to export');
    }
  };

  const saveCoupon = async () => {
    if (!couponForm.code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    setSavingCoupon(true);
    try {
      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, couponForm);
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${API}/admin/coupons`, couponForm);
        toast.success('Coupon created successfully');
      }
      setShowCouponModal(false);
      setEditingCoupon(null);
      resetCouponForm();
      fetchCoupons();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save coupon'));
    } finally {
      setSavingCoupon(false);
    }
  };

  const resetCouponForm = () => {
    setCouponForm({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      applicable_to: ['hackathon_entry_fee'],
      max_uses: null,
      max_uses_per_user: 1,
      valid_from: '',
      valid_until: '',
      is_active: true,
      description: ''
    });
  };

  const toggleCouponStatus = async (couponId) => {
    try {
      const response = await axios.put(`${API}/admin/coupons/${couponId}/toggle`);
      toast.success(response.data.message);
      fetchCoupons();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to toggle coupon status'));
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete coupon'));
    }
  };

  const toggleReferralStatus = async (referralId) => {
    try {
      const response = await axios.put(`${API}/admin/referrals/${referralId}/toggle`);
      toast.success(response.data.message);
      fetchReferrals();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to toggle referral status'));
    }
  };

  const updateDatePricing = async () => {
    setDatePricingLoading(true);
    try {
      await axios.put(`${API}/admin/settings/date-pricing`, datePricing);
      toast.success('Date-based pricing updated successfully!');
      setDatePricingSaved(true);
      setTimeout(() => setDatePricingSaved(false), 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update date pricing'));
    } finally {
      setDatePricingLoading(false);
    }
  };

  // Registration Control Functions
  const fetchRegistrationSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/registration-control`);
      setRegistrationSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch registration settings:', err);
    }
  };

  const toggleRegistrationSetting = async (settingKey) => {
    setRegistrationSettingsLoading(true);
    try {
      const response = await axios.put(`${API}/admin/settings/registration-control`, {
        setting: settingKey,
        enabled: !registrationSettings[settingKey]
      });
      setRegistrationSettings(prev => ({ ...prev, [settingKey]: !prev[settingKey] }));
      toast.success(response.data.message);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update registration setting'));
    } finally {
      setRegistrationSettingsLoading(false);
    }
  };

  // Email Communication Functions
  const fetchEmailLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-logs`);
      setEmailLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch email logs:', err);
    }
  };

  const sendBulkEmail = async () => {
    if (!emailSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!emailMessage.trim() || emailMessage.trim().length < 10) {
      toast.error('Please enter a message (at least 10 characters)');
      return;
    }
    if (emailRecipientType === 'selected' && selectedTeamIds.length === 0) {
      toast.error('Please select at least one team');
      return;
    }

    setEmailSending(true);
    try {
      const response = await axios.post(`${API}/admin/send-email`, {
        subject: emailSubject,
        message: emailMessage,
        recipient_type: emailRecipientType,
        selected_team_ids: selectedTeamIds
      });

      setEmailResult(response.data);
      setShowEmailResultModal(true);
      toast.success(`Emails sent: ${response.data.sent_count} successful, ${response.data.failed_count} failed`);
      
      // Clear form
      setEmailSubject('');
      setEmailMessage('');
      setSelectedTeamIds([]);
      
      // Refresh logs
      fetchEmailLogs();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send emails'));
    } finally {
      setEmailSending(false);
    }
  };

  const toggleTeamSelection = (teamId) => {
    setSelectedTeamIds(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const selectAllTeams = () => {
    if (registrations && registrations.length > 0) {
      setSelectedTeamIds(registrations.map(t => t.id));
    }
  };

  const deselectAllTeams = () => {
    setSelectedTeamIds([]);
  };

  // State for partial payment modal
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [selectedTeamForPayment, setSelectedTeamForPayment] = useState(null);
  const [partialPaymentData, setPartialPaymentData] = useState({
    paid_members: 1,
    amount_paid: 0,
    coupon_code: ''
  });

  const updatePaymentStatus = async (teamId, status, additionalData = {}) => {
    try {
      let url = `${API}/admin/team/${teamId}/payment?payment_status=${status}`;
      
      // Add additional parameters for partial payments
      if (status === 'partial' && additionalData.paid_members) {
        url += `&paid_members=${additionalData.paid_members}`;
        if (additionalData.amount_paid) {
          url += `&amount_paid=${additionalData.amount_paid}`;
        }
        if (additionalData.coupon_code) {
          url += `&coupon_code=${encodeURIComponent(additionalData.coupon_code)}`;
        }
      }
      
      await axios.put(url);
      toast.success(`Payment status updated to ${status}`);
      fetchRegistrations();
      setShowPartialPaymentModal(false);
      setSelectedTeamForPayment(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update payment status'));
    }
  };

  const deleteUnpaidTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete the unpaid team "${teamName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/team/${teamId}`);
      toast.success(`Team "${teamName}" deleted successfully`);
      fetchRegistrations();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete team'));
    }
  };

  const openPartialPaymentModal = (team) => {
    setSelectedTeamForPayment(team);
    setPartialPaymentData({
      paid_members: team.paid_member_count || 1,
      amount_paid: team.amount_paid || team.price_per_person,
      coupon_code: team.coupon_applied || ''
    });
    setShowPartialPaymentModal(true);
  };

  const handlePartialPaymentSubmit = () => {
    if (selectedTeamForPayment) {
      updatePaymentStatus(selectedTeamForPayment.id, 'partial', partialPaymentData);
    }
  };

  const exportToCSV = () => {
    // Create detailed CSV with all participant information
    const headers = [
      'Team Name',
      'Registration No',
      'Team Leader',
      'Total Members',
      'Payment Status',
      'Total Amount',
      'Price Per Person',
      'Participant ID',
      'Participant Name',
      'Email',
      'Phone',
      'College',
      'Workshop',
      'Mode'
    ];
    
    const rows = [];
    
    // Create a row for each team member
    registrations.forEach(team => {
      team.members.forEach(member => {
        rows.push([
          team.team_name,
          team.registration_number,
          team.leader_username,
          team.total_members,
          team.payment_status,
          team.total_price,
          team.price_per_person,
          member.participant_id || 'N/A',
          member.name,
          member.email,
          member.phone,
          member.college || 'N/A',
          member.workshop_choice || 'No Workshop',
          member.mode || 'N/A'
        ]);
      });
    });

    // Create CSV content
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      // Escape commas in data
      const escapedRow = row.map(cell => {
        const cellStr = String(cell);
        return cellStr.includes(',') ? `"${cellStr}"` : cellStr;
      });
      csv += escapedRow.join(',') + '\n';
    });

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evalentx_registrations_detailed_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setIsLoggedIn(false);
    navigate('/');
  };

  // Interview Credits functions
  const searchStudentsForCredits = async (query) => {
    if (!query || query.length < 2) { setCreditStudents([]); return; }
    setCreditSearchLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/arena-students?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditStudents(res.data.students || []);
    } catch (err) {
      toast.error('Failed to search students');
    } finally {
      setCreditSearchLoading(false);
    }
  };

  const grantInterviewCredits = async (studentId, studentName) => {
    if (creditAmount <= 0) { toast.error('Credits must be at least 1'); return; }
    setGrantingCredit(studentId);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/arena-students/${studentId}/interview-credits`, {
        credits: creditAmount,
        reason: creditReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      searchStudentsForCredits(creditSearch);
      fetchCreditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant credits');
    } finally {
      setGrantingCredit(null);
    }
  };

  const fetchCreditLogs = async () => {
    setCreditLogsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/interview-credit-logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch credit logs');
    } finally {
      setCreditLogsLoading(false);
    }
  };

  const logoutAllSessions = async () => {
    setLoggingOutAll(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/logout-all-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All sessions have been logged out successfully');
      setShowLogoutAllModal(false);
      // Log out the current session as well
      handleLogout();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to logout all sessions'));
    } finally {
      setLoggingOutAll(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-block bg-gradient-to-r from-red-600 to-red-700 text-white p-4 rounded-full mb-4">
              <Lock size={40} />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Login</h1>
            <p className="text-gray-600">Access the admin dashboard</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-8 border-2 border-red-100">
            {loginError && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{loginError}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="form-label">Admin Username</label>
                <input
                  type="text"
                  data-testid="admin-login-username-input"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div>
                <label className="form-label">Password</label>
                <input
                  type="password"
                  data-testid="admin-login-password-input"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="admin-login-submit-btn"
                disabled={loading}
                className="w-full bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login as Admin'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Vertical Sidebar - Hidden on mobile, shown as overlay when mobileMenuOpen */}
      <div className={`
        ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} 
        w-64
        bg-gradient-to-b from-slate-900 to-slate-800 
        h-screen fixed left-0 top-0 
        transition-all duration-300 z-50
        flex flex-col
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo/Header - Fixed at top */}
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            {(!sidebarCollapsed || mobileMenuOpen) && (
              <div>
                <h1 className="text-white font-bold text-lg">EvalentX</h1>
                <p className="text-slate-400 text-xs">Admin Panel</p>
              </div>
            )}
            {/* Close button for mobile */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700 lg:hidden"
            >
              <X size={20} />
            </button>
            {/* Collapse button for desktop */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700 hidden lg:block"
            >
              {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          </div>
        </div>

        {/* Navigation Items - Scrollable */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Sales Analytics */}
          <button
            onClick={() => { setActiveTab('sales'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'sales'
                ? 'bg-green-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Sales Analytics"
          >
            <BarChart3 size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Sales Analytics</span>}
          </button>

          {/* Registrations */}
          <button
            onClick={() => { setActiveTab('registrations'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'registrations'
                ? 'bg-blue-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Registrations"
          >
            <Users size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Registrations</span>}
          </button>

          {/* Hackathon Participant Manager */}
          <button
            onClick={() => { setActiveTab('hackathon-manager'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'hackathon-manager'
                ? 'bg-purple-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Hackathon Manager"
          >
            <Edit size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Edit Participants</span>}
          </button>

          {/* Event Requirements */}
          <button
            onClick={() => { setActiveTab('intents'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'intents'
                ? 'bg-blue-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Event Requirements"
          >
            <CheckCircle size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Event Requirements</span>}
          </button>

          {/* Accommodation & Food */}
          <button
            onClick={() => { setActiveTab('bookings'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'bookings'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Accommodation & Food"
          >
            <Home size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Bookings</span>}
          </button>

          {/* Team Submissions */}
          <button
            onClick={() => { setActiveTab('team-submissions'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'team-submissions'
                ? 'bg-teal-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Team Project Submissions"
          >
            <Layers size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Project Submissions</span>}
          </button>

          {/* Contact Submissions */}
          <button
            onClick={() => { setActiveTab('submissions'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'submissions'
                ? 'bg-green-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Contact Submissions"
          >
            <MessageSquare size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Contact Forms</span>}
          </button>

          {/* Certificates */}
          <button
            onClick={() => { setActiveTab('certificates'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'certificates'
                ? 'bg-amber-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Certificates"
          >
            <Award size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Certificates</span>}
          </button>

          {/* Communications */}
          <button
            onClick={() => { setActiveTab('communications'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'communications'
                ? 'bg-purple-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Communications"
          >
            <Mail size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Communications</span>}
          </button>

          <div className="border-t border-slate-700 my-3"></div>

          {/* Admin Team */}
          <button
            onClick={() => { setActiveTab('admin-team'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'admin-team'
                ? 'bg-cyan-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Admin Team"
          >
            <UserCog size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Admin Team</span>}
          </button>

          {/* Referrals */}
          <button
            onClick={() => { setActiveTab('referrals'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'referrals'
                ? 'bg-emerald-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Referral Partners"
          >
            <Share2 size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Referrals</span>}
          </button>

          {/* Messages (Contact Us & Investor) */}
          <button
            onClick={() => { setActiveTab('messages'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'messages'
                ? 'bg-teal-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Messages"
          >
            <MessageSquare size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Messages</span>}
          </button>

          {/* Problem Statements */}
          <button
            onClick={() => { setActiveTab('problem-statements'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'problem-statements'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Problem Statements"
          >
            <Target size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Problem Bank</span>}
          </button>

          {/* Jury Management */}
          <button
            onClick={() => { setActiveTab('jury-management'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'jury-management'
                ? 'bg-amber-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Jury Management"
          >
            <Gavel size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Jury</span>}
          </button>

          {/* Round Configuration */}
          <button
            onClick={() => { setActiveTab('round-config'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'round-config'
                ? 'bg-cyan-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Round Configuration"
          >
            <Layers size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Rounds</span>}
          </button>

          {/* Jury Assignments */}
          <button
            onClick={() => { setActiveTab('jury-assignments'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'jury-assignments'
                ? 'bg-teal-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Jury Assignments"
          >
            <UserCheck size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Assign Teams</span>}
          </button>

          {/* Settings */}
          <button
            onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'settings'
                ? 'bg-slate-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Settings"
          >
            <Settings size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Settings</span>}
          </button>

          {/* Super Admin Config */}
          <button
            onClick={() => { setActiveTab('super-config'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'super-config'
                ? 'bg-purple-600 text-white'
                : 'text-purple-300 hover:bg-purple-700/50 hover:text-white'
            }`}
            title="Super Admin Config"
            data-testid="super-config-tab"
          >
            <Zap size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Super Config</span>}
          </button>

          {/* Divider */}
          <div className="my-2 border-t border-slate-700"></div>

          {/* Team Registry (Unified) */}
          <button
            onClick={() => { setActiveTab('team-registry'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'team-registry'
                ? 'bg-pink-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Team Registry"
          >
            <Layers size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Team Registry</span>}
          </button>

          {/* POTD Management */}
          <button
            onClick={() => { setActiveTab('potd-management'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'potd-management'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="POTD Management"
          >
            <Code size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Code Arena</span>}
          </button>

          {/* Company Assessments */}
          <button
            onClick={() => { setActiveTab('company-assessments'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'company-assessments'
                ? 'bg-purple-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Company Assessments"
          >
            <Briefcase size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Company Portal</span>}
          </button>

          {/* EvalentX Arena Students */}
          <button
            onClick={() => { setActiveTab('codearena-students'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'codearena-students'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="EvalentX Arena Students"
          >
            <Users size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>EvalentX Arena Students</span>}
          </button>

          {/* EvalentX Arena Dashboard */}
          <button
            onClick={() => { setActiveTab('codearena-dashboard'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'codearena-dashboard'
                ? 'bg-cyan-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="EvalentX Arena Dashboard"
          >
            <Code size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>EvalentX Arena Platform</span>}
          </button>

          {/* EvalentX Arena Contests */}
          <button
            onClick={() => { setActiveTab('codearena-contests'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'codearena-contests'
                ? 'bg-purple-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="EvalentX Arena Contests"
          >
            <Trophy size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>EvalentX Arena Contests</span>}
          </button>

          {/* EvalentX Arena Certificates */}
          <button
            onClick={() => { setActiveTab('codearena-certificates'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'codearena-certificates'
                ? 'bg-amber-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="EvalentX Arena Certificates"
          >
            <Award size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>EvalentX Arena Certificates</span>}
          </button>

          {/* Job Approvals */}
          <button
            onClick={() => { setActiveTab('job-approvals'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'job-approvals'
                ? 'bg-rose-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Job Approvals"
          >
            <Briefcase size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Job Approvals</span>}
          </button>

          {/* Email Approval */}
          <button
            onClick={() => { navigate('/admin/email-approval'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-slate-300 hover:bg-slate-700 hover:text-white`}
            title="Company Email Approval"
          >
            <Mail size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Email Approval</span>}
          </button>

          {/* Enterprise Hosting */}
          <button
            onClick={() => { setActiveTab('enterprise-hosting'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'enterprise-hosting'
                ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Enterprise Hosting"
          >
            <Trophy size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Enterprise Hosting</span>}
          </button>

          {/* Resume Upgrades */}
          <button
            onClick={() => { setActiveTab('resume-upgrades'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'resume-upgrades'
                ? 'bg-orange-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Resume Upgrades"
          >
            <FileText size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Resume Upgrades</span>}
          </button>

          {/* Mock Interview Credits */}
          <button
            onClick={() => { setActiveTab('interview-credits'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'interview-credits'
                ? 'bg-blue-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Interview Credits"
            data-testid="interview-credits-tab"
          >
            <MessageSquare size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Interview Credits</span>}
          </button>

          {/* Verified Profile Admin */}
          <button
            onClick={() => { navigate('/admin/verified-talent'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-slate-300 hover:bg-slate-700 hover:text-white"
            title="Verified Profile Admin"
            data-testid="verified-profile-admin-tab"
          >
            <CheckCircle size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Verified Profile</span>}
          </button>

          {/* Future Skills */}
          <button
            onClick={() => { setActiveTab('future-skills'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'future-skills'
                ? 'bg-pink-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="School of Future Skills"
          >
            <BookOpen size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Future Skills</span>}
          </button>

          {/* Colleges Management */}
          <button
            onClick={() => { setActiveTab('colleges'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'colleges'
                ? 'bg-emerald-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="College Management"
          >
            <GraduationCap size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Colleges</span>}
          </button>

          {/* Investor Connect Submissions */}
          <button
            onClick={() => { setActiveTab('investor-connect'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'investor-connect'
                ? 'bg-emerald-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Investor Connect"
          >
            <Trophy size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Investor Connect</span>}
          </button>

          {/* Event Hosting */}
          <button
            onClick={() => { setActiveTab('event-hosting'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'event-hosting'
                ? 'bg-emerald-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Event Hosting"
          >
            <Calendar size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Event Hosting</span>}
          </button>

          {/* Super Admin */}
          <button
            onClick={() => { setActiveTab('super-admin'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
              activeTab === 'super-admin'
                ? 'bg-purple-600 text-white'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
            }`}
            title="Super Admin"
            data-testid="super-admin-tab"
          >
            <Shield size={20} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span>Super Admin</span>}
          </button>
        </nav>

        {/* Footer Actions - Fixed at bottom */}
        <div className="flex-shrink-0 p-3 border-t border-slate-700 space-y-2 bg-slate-900">
          <button
            onClick={() => setShowLogoutAllModal(true)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-amber-400 hover:bg-slate-700 transition-all ${sidebarCollapsed && !mobileMenuOpen ? 'justify-center' : ''}`}
            title="Logout All Devices"
          >
            <Shield size={18} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span className="text-sm">Logout All</span>}
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-slate-700 transition-all ${sidebarCollapsed && !mobileMenuOpen ? 'justify-center' : ''}`}
            title="Logout"
          >
            <LogOut size={18} />
            {(!sidebarCollapsed || mobileMenuOpen) && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 min-w-0 ml-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b px-4 lg:px-6 py-4 sticky top-0 z-40">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                data-testid="mobile-menu-btn"
              >
                <Menu size={24} />
              </button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-gray-800">
                  {activeTab === 'sales' && 'Sales Analytics'}
                  {activeTab === 'registrations' && 'Registrations'}
                  {activeTab === 'hackathon-manager' && 'Hackathon Participant Manager'}
                  {activeTab === 'intents' && 'Event Requirements'}
                  {activeTab === 'bookings' && 'Bookings'}
                  {activeTab === 'team-submissions' && 'Project Submissions'}
                  {activeTab === 'submissions' && 'Contact Forms'}
                  {activeTab === 'certificates' && 'Certificates'}
                  {activeTab === 'communications' && 'Communications'}
                  {activeTab === 'admin-team' && 'Admin Team'}
                  {activeTab === 'referrals' && 'Referral Partners'}
                  {activeTab === 'messages' && 'Contact Messages'}
                  {activeTab === 'problem-statements' && 'Problem Statement Bank'}
                  {activeTab === 'jury-management' && 'Jury Management'}
                  {activeTab === 'round-config' && 'Round Configuration'}
                  {activeTab === 'jury-assignments' && 'Jury Team Assignments'}
                  {activeTab === 'settings' && 'Settings'}
                  {activeTab === 'super-config' && 'Super Admin Configuration'}
                  {activeTab === 'team-registry' && 'Team Registry'}
                  {activeTab === 'potd-management' && 'Code Arena - POTD Management'}
                  {activeTab === 'investor-connect' && 'Investor Connect Submissions'}
                  {activeTab === 'company-assessments' && 'Company Assessment Portal'}
                  {activeTab === 'codearena-students' && 'EvalentX Arena Students'}
                  {activeTab === 'codearena-dashboard' && 'EvalentX Arena Platform'}
                  {activeTab === 'codearena-contests' && 'EvalentX Arena Contests'}
                  {activeTab === 'codearena-certificates' && 'EvalentX Arena Certificates'}
                  {activeTab === 'job-approvals' && 'Job Approvals'}
                  {activeTab === 'enterprise-hosting' && 'Enterprise Challenge Hosting'}
                  {activeTab === 'resume-upgrades' && 'Resume Upgrades'}
                  {activeTab === 'interview-credits' && 'Athena AI Interview Credits'}
                  {activeTab === 'future-skills' && 'School of Future Skills'}
                  {activeTab === 'colleges' && 'College Management'}
                  {activeTab === 'event-hosting' && 'Event Hosting Management'}
                  {activeTab === 'super-admin' && 'Super Admin Panel'}
                </h1>
                <p className="text-xs lg:text-sm text-gray-500 hidden sm:block">EvalentX IIT Gandhinagar 2026</p>
              </div>
            </div>
            {stats && (
              <div className="flex gap-2 lg:gap-4 text-xs lg:text-sm">
                <div className="bg-blue-50 px-2 lg:px-3 py-1 rounded-lg">
                  <span className="text-gray-600 hidden sm:inline">Teams:</span> <span className="font-bold text-blue-600">{stats.total_teams}</span>
                </div>
                <div className="bg-green-50 px-2 lg:px-3 py-1 rounded-lg">
                  <span className="text-gray-600 hidden sm:inline">Paid:</span> <span className="font-bold text-green-600">{stats.paid_teams}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 lg:p-6">
          {/* Sales Analytics Tab */}
          {activeTab === 'sales' && (
            <div className="space-y-4 lg:space-y-6">
              {/* Quick Date Filters */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  Quick Filters
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { label: 'Today', days: 0 },
                    { label: 'Yesterday', days: 1 },
                    { label: 'Last 7 Days', days: 7 },
                    { label: 'Last 30 Days', days: 30 },
                    { label: 'Last 90 Days', days: 90 },
                    { label: 'All Time', days: -1 }
                  ].map((filter) => {
                    const isActive = (() => {
                      if (filter.days === -1) return !salesDateRange.start && !salesDateRange.end;
                      const today = new Date();
                      if (filter.days === 0) {
                        return salesDateRange.start === today.toISOString().split('T')[0] && salesDateRange.end === today.toISOString().split('T')[0];
                      }
                      if (filter.days === 1) {
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        return salesDateRange.start === yesterday.toISOString().split('T')[0] && salesDateRange.end === yesterday.toISOString().split('T')[0];
                      }
                      const startDate = new Date(today);
                      startDate.setDate(startDate.getDate() - filter.days);
                      return salesDateRange.start === startDate.toISOString().split('T')[0] && salesDateRange.end === today.toISOString().split('T')[0];
                    })();
                    
                    return (
                      <button
                        key={filter.label}
                        onClick={() => {
                          const today = new Date();
                          if (filter.days === -1) {
                            setSalesDateRange({ start: '', end: '' });
                          } else if (filter.days === 0) {
                            const dateStr = today.toISOString().split('T')[0];
                            setSalesDateRange({ start: dateStr, end: dateStr });
                          } else if (filter.days === 1) {
                            const yesterday = new Date(today);
                            yesterday.setDate(yesterday.getDate() - 1);
                            const dateStr = yesterday.toISOString().split('T')[0];
                            setSalesDateRange({ start: dateStr, end: dateStr });
                          } else {
                            const startDate = new Date(today);
                            startDate.setDate(startDate.getDate() - filter.days);
                            setSalesDateRange({ start: startDate.toISOString().split('T')[0], end: today.toISOString().split('T')[0] });
                          }
                          setTimeout(fetchSalesAnalytics, 100);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
                </div>
                
                {/* Custom Date Range */}
                <div className="flex flex-wrap gap-4 items-end pt-4 border-t">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={salesDateRange.start}
                      onChange={(e) => setSalesDateRange({ ...salesDateRange, start: e.target.value })}
                      className="px-4 py-2 border rounded-lg focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={salesDateRange.end}
                      onChange={(e) => setSalesDateRange({ ...salesDateRange, end: e.target.value })}
                      className="px-4 py-2 border rounded-lg focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={fetchSalesAnalytics}
                    disabled={salesLoading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {salesLoading ? 'Loading...' : 'Apply'}
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              {salesData && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 lg:p-6 text-white shadow-lg">
                      <TrendingUp className="mb-2 lg:mb-3" size={24} />
                      <p className="text-green-100 text-xs lg:text-sm mb-1">Total Gross Sales</p>
                      <p className="text-xl lg:text-3xl font-bold">₹{salesData.summary.total_gross.toLocaleString()}</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 lg:p-6 text-white shadow-lg">
                      <DollarSign className="mb-2 lg:mb-3" size={24} />
                      <p className="text-red-100 text-xs lg:text-sm mb-1">Gateway Fee ({salesData.summary.gateway_fee_percent}%)</p>
                      <p className="text-xl lg:text-3xl font-bold">- ₹{salesData.summary.gateway_deduction.toLocaleString()}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 lg:p-6 text-white shadow-lg">
                      <CheckCircle className="mb-2 lg:mb-3" size={24} />
                      <p className="text-blue-100 text-xs lg:text-sm mb-1">Net Amount</p>
                      <p className="text-xl lg:text-3xl font-bold">₹{salesData.summary.net_amount.toLocaleString()}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 lg:p-6 text-white shadow-lg">
                      <Users className="mb-2 lg:mb-3" size={24} />
                      <p className="text-purple-100 text-xs lg:text-sm mb-1">Total Transactions</p>
                      <p className="text-xl lg:text-3xl font-bold">{salesData.summary.total_transactions}</p>
                    </div>
                  </div>

                  {/* Service Breakdown */}
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <PieChart size={20} />
                      Sales by Service
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                      {Object.entries(salesData.by_service).filter(([_, service]) => service.visible !== false).map(([key, service]) => (
                        <div key={key} className={`bg-gray-50 rounded-lg p-3 lg:p-4 border-l-4 ${
                          key === 'hackathon' ? 'border-blue-500' :
                          key === 'accommodation_food' ? 'border-indigo-500' :
                          key === 'accommodation_only' ? 'border-cyan-500' :
                          'border-orange-500'
                        }`}>
                          <p className="text-xs lg:text-sm text-gray-600 mb-1">{service.label}</p>
                          <p className="text-2xl font-bold text-gray-800">₹{service.gross_amount.toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {service.count} transactions • Net: ₹{service.net_amount.toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Daily Chart */}
                  {salesData.daily_chart_data.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <BarChart3 size={20} />
                        Daily Sales Trend
                      </h3>
                      <div className="overflow-x-auto">
                        <div className="min-w-full">
                          <div className="flex items-end gap-2 h-48">
                            {salesData.daily_chart_data.slice(-30).map((day, idx) => {
                              const maxValue = Math.max(...salesData.daily_chart_data.map(d => d.total)) || 1;
                              const height = (day.total / maxValue) * 100;
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center min-w-[30px]">
                                  <div 
                                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all hover:from-blue-600 hover:to-blue-500 cursor-pointer relative group"
                                    style={{ height: `${Math.max(height, 5)}%` }}
                                  >
                                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                                      {day.date}: ₹{day.total.toLocaleString()}
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-2 whitespace-nowrap">{day.date.slice(5)}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Transaction List */}
                  {salesData.transactions && salesData.transactions.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="p-6 border-b">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <FileText size={20} />
                          Recent Transactions ({salesData.transactions.length})
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team / User</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Members</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {salesData.transactions.map((txn, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-600">{txn.date}</td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900">{txn.team_name}</div>
                                  <div className="text-xs text-gray-500">{txn.leader_name}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    txn.service_key === 'hackathon' ? 'bg-blue-100 text-blue-700' :
                                    txn.service_key === 'accommodation_food' ? 'bg-indigo-100 text-indigo-700' :
                                    txn.service_key === 'accommodation_only' ? 'bg-cyan-100 text-cyan-700' :
                                    'bg-orange-100 text-orange-700'
                                  }`}>
                                    {txn.service}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{txn.members}</td>
                                <td className="px-4 py-3 text-right font-semibold text-green-600">₹{txn.amount.toLocaleString()}</td>
                                <td className="px-4 py-3 text-xs font-mono text-gray-500">{txn.transaction_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Restricted Services Notice */}
                  {!salesData.is_super_admin && salesData.allowed_services && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                        <div>
                          <p className="font-medium text-amber-800">Limited Sales View</p>
                          <p className="text-sm text-amber-700">
                            You can only view sales data for: {salesData.allowed_services.map(s => s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ')}.
                            Contact a Super Admin to expand your access.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {salesLoading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                </div>
              )}
            </div>
          )}

          {/* Admin Team Tab */}
          {activeTab === 'admin-team' && (
            <div className="space-y-6">
              {/* Add Admin Button */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Manage Admin Users</h3>
                <button
                  onClick={() => setShowAddAdminModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  Add Admin User
                </button>
              </div>

              {/* Admin List */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {adminTeam.map((admin) => (
                      <tr key={admin.username} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{admin.username}</td>
                        <td className="px-6 py-4 text-gray-600">{admin.name}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            admin.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                            admin.role === 'view_only' ? 'bg-blue-100 text-blue-700' :
                            admin.role === 'edit_only' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {admin.role?.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            admin.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {admin.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleAdminStatus(admin.username, admin.is_active !== false)}
                              className={`p-2 rounded-lg ${admin.is_active !== false ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                              title={admin.is_active !== false ? 'Deactivate' : 'Activate'}
                            >
                              {admin.is_active !== false ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                            </button>
                            <button
                              onClick={() => deleteAdminUser(admin.username)}
                              className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {adminTeam.length === 0 && !adminTeamLoading && (
                  <div className="text-center py-8 text-gray-500">No admin users found</div>
                )}
                {adminTeamLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                  </div>
                )}
              </div>

              {/* Role Legend */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-gray-800 mb-4">Role Permissions</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="font-semibold text-purple-700 mb-2">Super Admin</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Full access to all features</li>
                      <li>• Can manage admin team</li>
                      <li>• Can view/edit all settings</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-semibold text-green-700 mb-2">Edit Only</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Can view all data</li>
                      <li>• Can edit registrations/bookings</li>
                      <li>• Cannot manage admin team</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-blue-700 mb-2">View Only</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Can view all data</li>
                      <li>• Cannot make any changes</li>
                      <li>• Cannot manage admin team</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Registrations Tab */}
          {activeTab === 'registrations' && (
            <>
              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-6 mb-6 lg:mb-8">
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 border-blue-100">
                    <Users className="text-blue-600 mb-2 lg:mb-3" size={24} />
                    <p className="text-gray-600 text-xs lg:text-sm mb-1">Total Teams</p>
                    <p className="text-xl lg:text-3xl font-bold text-gray-800">{stats.total_teams}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 border-blue-100">
                    <Users className="text-indigo-600 mb-2 lg:mb-3" size={24} />
                    <p className="text-gray-600 text-xs lg:text-sm mb-1">Total Participants</p>
                    <p className="text-xl lg:text-3xl font-bold text-gray-800">{stats.total_participants}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 border-green-100">
                    <CheckCircle className="text-green-600 mb-2 lg:mb-3" size={24} />
                    <p className="text-gray-600 text-xs lg:text-sm mb-1">Paid Teams</p>
                    <p className="text-xl lg:text-3xl font-bold text-green-600">{stats.paid_teams}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 border-orange-100">
                    <Clock className="text-orange-600 mb-2 lg:mb-3" size={24} />
                    <p className="text-gray-600 text-xs lg:text-sm mb-1">Partial Payment</p>
                    <p className="text-xl lg:text-3xl font-bold text-orange-600">{stats.partial_teams || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 border-yellow-100">
                    <XCircle className="text-yellow-600 mb-2 lg:mb-3" size={24} />
                    <p className="text-gray-600 text-xs lg:text-sm mb-1">Pending</p>
                    <p className="text-xl lg:text-3xl font-bold text-yellow-600">{stats.pending_teams}</p>
                  </div>
                </div>
              )}

              {/* Paid Team Analytics Section */}
              {stats?.paid_team_analytics && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 lg:p-6 shadow-lg border-2 border-green-200 mb-6 lg:mb-8">
                  <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="text-green-600" size={24} />
                    Paid Teams Analytics
                    <span className="text-sm font-normal text-gray-500 ml-2">({stats.paid_teams} teams, {stats.paid_team_analytics.total_paid_members} members)</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                    {/* Workshop Stats */}
                    <div className="bg-white rounded-xl p-4 shadow-md">
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <BookOpen className="text-blue-600" size={18} />
                        Workshop Choices
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(stats.paid_team_analytics.workshop_stats).map(([workshop, count]) => (
                          <div key={workshop} className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">{workshop}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 lg:w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${stats.paid_team_analytics.total_paid_members > 0 ? (count / stats.paid_team_analytics.total_paid_members * 100) : 0}%` }}
                                />
                              </div>
                              <span className="font-bold text-gray-800 text-sm w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hackathon Themes */}
                    <div className="bg-white rounded-xl p-4 shadow-md">
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Code className="text-purple-600" size={18} />
                        Hackathon Themes
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(stats.paid_team_analytics.hackathon_themes).map(([theme, count]) => (
                          <div key={theme} className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 truncate max-w-[120px]" title={theme}>{theme}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 lg:w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-purple-500 rounded-full transition-all"
                                  style={{ width: `${stats.paid_teams > 0 ? (count / stats.paid_teams * 100) : 0}%` }}
                                />
                              </div>
                              <span className="font-bold text-gray-800 text-sm w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Workshop Mode */}
                    <div className="bg-white rounded-xl p-4 shadow-md">
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Monitor className="text-cyan-600" size={18} />
                        Workshop Mode (Online vs Offline)
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(stats.paid_team_analytics.mode_stats).map(([mode, count]) => (
                          <div key={mode} className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">{mode}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 lg:w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${mode === 'Online' ? 'bg-cyan-500' : mode === 'Offline' ? 'bg-teal-500' : 'bg-gray-400'}`}
                                  style={{ width: `${stats.paid_team_analytics.total_paid_members > 0 ? (count / stats.paid_team_analytics.total_paid_members * 100) : 0}%` }}
                                />
                              </div>
                              <span className="font-bold text-gray-800 text-sm w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

        {/* Export Button */}
        <div className="mb-6">
          <button
            onClick={exportToCSV}
            className="btn-primary flex items-center gap-2"
          >
            <Download size={20} />
            Export to CSV
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="mb-6 bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Search & Filters</h3>
          
          {/* Search Row */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Email Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Search by Leader Email
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Enter email address..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>
            </div>

            {/* Phone Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Search by Leader Phone
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Enter phone number..."
                  value={phoneSearchQuery}
                  onChange={(e) => handlePhoneSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Payment Status Filter */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Filter by Payment Status
            </label>
            <div className="flex flex-wrap gap-2 lg:gap-3">
              <button
                onClick={() => handlePaymentFilterChange('all')}
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  paymentStatusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All ({registrations.length})
              </button>
              <button
                onClick={() => handlePaymentFilterChange('completed')}
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  paymentStatusFilter === 'completed'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Paid ({registrations.filter(t => t.payment_status === 'completed').length})
              </button>
              <button
                onClick={() => handlePaymentFilterChange('partial')}
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  paymentStatusFilter === 'partial'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Partial ({registrations.filter(t => t.payment_status === 'partial').length})
              </button>
              <button
                onClick={() => handlePaymentFilterChange('pending')}
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  paymentStatusFilter === 'pending'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Unpaid ({registrations.filter(t => t.payment_status === 'pending').length})
              </button>
            </div>
          </div>

          {/* Member Count Filter */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Filter by Team Size
            </label>
            <div className="flex flex-wrap gap-2 lg:gap-3">
              <button
                onClick={() => setMemberCountFilter('all')}
                data-testid="filter-members-all"
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  memberCountFilter === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Sizes
              </button>
              <button
                onClick={() => setMemberCountFilter('1-3')}
                data-testid="filter-members-1-3"
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  memberCountFilter === '1-3'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                1-3 Members ({registrations.filter(t => t.total_members >= 1 && t.total_members <= 3).length})
              </button>
              <button
                onClick={() => setMemberCountFilter('4-6')}
                data-testid="filter-members-4-6"
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  memberCountFilter === '4-6'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                4-6 Members ({registrations.filter(t => t.total_members >= 4 && t.total_members <= 6).length})
              </button>
              <button
                onClick={() => setMemberCountFilter('7-10')}
                data-testid="filter-members-7-10"
                className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm lg:text-base ${
                  memberCountFilter === '7-10'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                7-10 Members ({registrations.filter(t => t.total_members >= 7 && t.total_members <= 10).length})
              </button>
            </div>
          </div>

          {/* Results Summary & Clear */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-4 border-t-2 border-gray-200">
            <div className="text-xs lg:text-sm text-gray-600">
              Showing <span className="font-bold text-blue-600">{filteredRegistrations.length}</span> of <span className="font-bold">{registrations.length}</span> teams
              {(searchQuery || phoneSearchQuery || paymentStatusFilter !== 'all' || memberCountFilter !== 'all') && (
                <span className="ml-2 text-gray-500">(filtered)</span>
              )}
            </div>
            {(searchQuery || phoneSearchQuery || paymentStatusFilter !== 'all' || memberCountFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPhoneSearchQuery('');
                  setPaymentStatusFilter('all');
                  setMemberCountFilter('all');
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold text-sm"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Registrations Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-blue-100">
          <div className="p-4 lg:p-6 bg-gradient-to-r from-iit-blue to-blue-700 text-white">
            <h2 className="text-lg lg:text-2xl font-bold">All Registrations</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('registration_number')}
                  >
                    <div className="flex items-center gap-2">
                      Reg #
                      {sortBy === 'registration_number' && (
                        <span className="text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700">Team Name</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700 hidden md:table-cell">Leader</th>
                  <th 
                    className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors hidden lg:table-cell"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      Registered
                      {sortBy === 'created_at' && (
                        <span className="text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700">Members</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700 hidden sm:table-cell">Paid / Due</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700 hidden lg:table-cell">Coupon</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getPaginatedRegistrations().map((team) => (
                  <tr key={team.id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm text-gray-800">#{team.registration_number}</td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm">
                      <button
                        onClick={() => handleViewTeamDetails(team)}
                        className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors text-left"
                      >
                        {team.team_name}
                      </button>
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm text-gray-600 hidden md:table-cell">{team.leader_username}</td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm text-gray-600 hidden lg:table-cell">
                      <div className="flex flex-col">
                        <span className="font-semibold">
                          {team.created_at ? new Date(team.created_at).toLocaleDateString('en-IN', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          }) : 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {team.created_at ? new Date(team.created_at).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          }) : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-800">{team.total_members} total</span>
                        {team.paid_member_count > 0 && team.paid_member_count < team.total_members && (
                          <span className="text-xs text-green-600">{team.paid_member_count} paid</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm font-semibold text-gray-800">₹{team.total_price?.toLocaleString()}</td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm hidden sm:table-cell">
                      <div className="flex flex-col gap-1">
                        <span className="text-green-600 font-semibold">
                          ₹{(team.amount_paid || 0).toLocaleString()}
                        </span>
                        <span className={`font-semibold ${team.amount_due > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          ₹{(team.amount_due || 0).toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm hidden lg:table-cell">
                      {team.coupon_applied ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                          {team.coupon_applied}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4">
                      <span className={`px-2 lg:px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        team.payment_status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : team.payment_status === 'partial'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {team.payment_status === 'completed' ? '✓ Paid' : team.payment_status === 'partial' ? '◐ Partial' : '⏳ Pending'}
                      </span>
                    </td>
                    <td className="px-3 lg:px-6 py-3 lg:py-4">
                      <div className="flex flex-wrap gap-1">
                        {team.payment_status === 'pending' && (
                          <>
                            <button
                              onClick={() => updatePaymentStatus(team.id, 'completed')}
                              className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-green-700"
                              title="Mark as fully paid"
                            >
                              Full
                            </button>
                            <button
                              onClick={() => openPartialPaymentModal(team)}
                              className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-orange-600 hidden sm:inline-block"
                              title="Mark partial payment"
                            >
                              Partial
                            </button>
                            <button
                              onClick={() => deleteUnpaidTeam(team.id, team.team_name)}
                              className="bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-red-600"
                              title="Delete unpaid team"
                            >
                              Del
                            </button>
                          </>
                        )}
                        {team.payment_status === 'partial' && (
                          <>
                            <button
                              onClick={() => updatePaymentStatus(team.id, 'completed')}
                              className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-green-700"
                              title="Mark as fully paid"
                            >
                              Full Pay
                            </button>
                            <button
                              onClick={() => openPartialPaymentModal(team)}
                              className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-orange-600"
                              title="Edit partial payment"
                            >
                              Edit
                            </button>
                          </>
                        )}
                        {team.payment_status === 'completed' && (
                          <button
                            onClick={() => updatePaymentStatus(team.id, 'pending')}
                            className="bg-yellow-600 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-yellow-700"
                            title="Revert to pending"
                          >
                            Unpay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRegistrations.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500 text-lg">
                  {searchQuery ? `No teams found with leader email containing "${searchQuery}"` : 'No registrations yet'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilteredRegistrations(registrations);
                    }}
                    className="mt-4 text-blue-600 hover:underline font-semibold"
                  >
                    Clear search and show all teams
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredRegistrations.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100 mt-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                {/* Records per page selector */}
                <div className="flex items-center gap-3">
                  <label className="text-sm font-semibold text-gray-700">Show:</label>
                  <select
                    value={recordsPerPage}
                    onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 font-semibold"
                  >
                    <option value={10}>10 records</option>
                    <option value={50}>50 records</option>
                    <option value={100}>100 records</option>
                  </select>
                </div>

                {/* Pagination info */}
                <div className="text-sm text-gray-600">
                  Showing <span className="font-bold text-blue-600">{((currentPage - 1) * recordsPerPage) + 1}</span> to{' '}
                  <span className="font-bold text-blue-600">
                    {Math.min(currentPage * recordsPerPage, filteredRegistrations.length)}
                  </span>{' '}
                  of <span className="font-bold">{filteredRegistrations.length}</span> teams
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index + 1;
                      } else if (currentPage <= 3) {
                        pageNum = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + index;
                      } else {
                        pageNum = currentPage - 2 + index;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border-2 border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {/* Hackathon Participant Manager Tab */}
        {activeTab === 'hackathon-manager' && (
          <HackathonParticipantManager />
        )}

        {/* Certificates Tab */}
        {activeTab === 'intents' && (
          <div>
            {/* Summary Cards */}
            {intentSummary && (
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100">
                  <div className="text-sm text-gray-600 mb-1">Total Paid Teams</div>
                  <div className="text-3xl font-bold text-blue-600">{intentSummary.total_paid_teams}</div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-100">
                  <div className="text-sm text-gray-600 mb-1">Intent Submitted</div>
                  <div className="text-3xl font-bold text-green-600">{intentSummary.teams_with_intent}</div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-red-100">
                  <div className="text-sm text-gray-600 mb-1">Pending Submission</div>
                  <div className="text-3xl font-bold text-red-600">{intentSummary.teams_without_intent}</div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-100">
                  <div className="text-sm text-gray-600 mb-1">Completion Rate</div>
                  <div className="text-3xl font-bold text-purple-600">
                    {Math.round((intentSummary.teams_with_intent / intentSummary.total_paid_teams) * 100)}%
                  </div>
                </div>
              </div>
            )}

            {/* Requirements Summary */}
            {intentSummary && (
              <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Total Requirements</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                    <div className="bg-purple-600 p-3 rounded-full">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{intentSummary.total_accommodation_required}</div>
                      <div className="text-sm text-gray-600">Accommodation</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-orange-50 rounded-lg">
                    <div className="bg-orange-600 p-3 rounded-full">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{intentSummary.total_food_required}</div>
                      <div className="text-sm text-gray-600">Food</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                    <div className="bg-green-600 p-3 rounded-full">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{intentSummary.total_merchandise_required}</div>
                      <div className="text-sm text-gray-600">Merchandise</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Intents Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-blue-100">
              <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <h2 className="text-2xl font-bold">Team Requirements Submissions</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Reg #</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Team Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Leader</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Team Size</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Accommodation</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Food</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Merchandise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {intents.map((team) => (
                      <tr key={team.team_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-600">{team.registration_number}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">{team.team_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{team.leader_username}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{team.total_members}</td>
                        <td className="px-6 py-4 text-sm">
                          {team.intent_submitted ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              ✓ Submitted
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {team.intent_data ? (
                            team.intent_data.accommodation_required ? (
                              <span className="text-green-600 font-semibold">
                                ✓ {team.intent_data.accommodation_count}
                              </span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {team.intent_data ? (
                            team.intent_data.food_required ? (
                              <span className="text-green-600 font-semibold">
                                ✓ {team.intent_data.food_count}
                              </span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {team.intent_data ? (
                            team.intent_data.merchandise_required ? (
                              <span className="text-green-600 font-semibold">
                                ✓ {team.intent_data.merchandise_count}
                              </span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            {/* Bookings Summary */}
            {bookingSummary && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-indigo-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-indigo-100 p-2 rounded-full">
                      <Home className="text-indigo-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Accom + Food</p>
                      <p className="text-lg font-bold text-indigo-600">{bookingSummary.accommodation_food_bookings}</p>
                      <p className="text-xs text-gray-500">{bookingSummary.total_members_accommodation} pax</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-purple-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-purple-100 p-2 rounded-full">
                      <Home className="text-purple-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Accom Only</p>
                      <p className="text-lg font-bold text-purple-600">{bookingSummary.accommodation_only_bookings || 0}</p>
                      <p className="text-xs text-gray-500">{bookingSummary.total_members_accommodation_only || 0} pax</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-orange-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-orange-100 p-2 rounded-full">
                      <Utensils className="text-orange-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Food Only</p>
                      <p className="text-lg font-bold text-orange-600">{bookingSummary.food_only_bookings}</p>
                      <p className="text-xs text-gray-500">{bookingSummary.total_members_food_only} pax</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-green-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-green-100 p-2 rounded-full">
                      <CheckCircle className="text-green-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Paid</p>
                      <p className="text-lg font-bold text-green-600">{bookingSummary.paid_bookings}</p>
                      <p className="text-xs text-green-500">Pass enabled</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-yellow-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-yellow-100 p-2 rounded-full">
                      <XCircle className="text-yellow-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Pending</p>
                      <p className="text-lg font-bold text-yellow-600">{bookingSummary.pending_bookings}</p>
                      <p className="text-xs text-yellow-500">Awaiting</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-blue-200">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Users className="text-blue-600" size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Total Members</p>
                      <p className="text-lg font-bold text-blue-600">
                        {(bookingSummary.total_members_accommodation || 0) + (bookingSummary.total_members_accommodation_only || 0) + (bookingSummary.total_members_food_only || 0)}
                      </p>
                      <p className="text-xs text-gray-500">all bookings</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bookings Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 md:p-6 border-b border-gray-100">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">All Bookings</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        View and manage accommodation & food bookings
                      </p>
                    </div>
                    {/* Export Button */}
                    <button
                      onClick={() => handleExportBookings('csv')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                      data-testid="export-bookings-btn"
                    >
                      <Download size={18} />
                      <span>Export to CSV</span>
                    </button>
                  </div>
                  
                  {/* Filters Row */}
                  <div className="flex flex-wrap gap-3">
                    {/* Booking Type Filter */}
                    <select
                      value={bookingTypeFilter}
                      onChange={(e) => setBookingTypeFilter(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 text-sm font-medium"
                      data-testid="booking-type-filter"
                    >
                      <option value="all">All Types</option>
                      <option value="accommodation_food">🏠 Accommodation + Food</option>
                      <option value="accommodation_only">🛏️ Accommodation Only</option>
                      <option value="food_only">🍽️ Food Only</option>
                    </select>
                    {/* Payment Status Filter */}
                    <select
                      value={bookingPaymentFilter}
                      onChange={(e) => setBookingPaymentFilter(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 text-sm font-medium"
                      data-testid="booking-payment-filter"
                    >
                      <option value="all">All Status</option>
                      <option value="completed">✓ Paid</option>
                      <option value="pending">⏳ Pending</option>
                    </select>
                    
                    {/* Filter info */}
                    <div className="flex items-center text-sm text-gray-500">
                      <span>
                        Showing {bookings
                          .filter(b => bookingTypeFilter === 'all' || b.booking_type === bookingTypeFilter)
                          .filter(b => bookingPaymentFilter === 'all' || b.payment_status === bookingPaymentFilter).length} of {bookings.length} bookings
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {bookings.length === 0 ? (
                <div className="p-12 text-center">
                  <Home className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No bookings yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Booking ID</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Team</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Members</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pass Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created At</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bookings
                        .filter(b => bookingTypeFilter === 'all' || b.booking_type === bookingTypeFilter)
                        .filter(b => bookingPaymentFilter === 'all' || b.payment_status === bookingPaymentFilter)
                        .map((booking) => (
                        <tr key={booking.booking_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <span className="font-mono font-semibold text-indigo-600">{booking.booking_id}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-gray-800">{booking.team_name}</p>
                              <p className="text-xs text-gray-500">{booking.leader_email}</p>
                              <p className="text-xs text-gray-400">{booking.leader_phone}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              booking.booking_type === 'accommodation_food'
                                ? 'bg-indigo-100 text-indigo-700'
                                : booking.booking_type === 'accommodation_only'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {booking.booking_type === 'accommodation_food' 
                                ? '🏠 Accommodation + Food' 
                                : booking.booking_type === 'accommodation_only'
                                ? '🛏️ Accommodation Only'
                                : '🍽️ Food Only'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold">{booking.member_count}</span>
                            <span className="text-xs text-gray-500 ml-1">members</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              booking.payment_status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {booking.payment_status === 'completed' ? '✓ Paid' : '⏳ Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {booking.payment_status === 'completed' ? (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                📄 Pass Available
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                                🔒 Locked
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {new Date(booking.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewBookingDetails(booking)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                              {booking.payment_status === 'pending' ? (
                                <button
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'completed')}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                  title="Mark as Paid"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'pending')}
                                  className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                                  title="Mark as Pending"
                                >
                                  <XCircle size={18} />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteBooking(booking.booking_id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Delete Booking"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Booking Details Modal */}
        {showBookingDetailsModal && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">Booking Details</h2>
                    <p className="text-indigo-200 font-mono">{selectedBooking.booking_id}</p>
                  </div>
                  <button 
                    onClick={() => setShowBookingDetailsModal(false)} 
                    className="text-white hover:text-gray-200"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Team Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Team Name</p>
                    <p className="font-bold text-gray-800">{selectedBooking.team_name}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Booking Type</p>
                    <p className="font-bold text-gray-800">
                      {selectedBooking.booking_type === 'accommodation_food' 
                        ? 'Accommodation + Food' 
                        : selectedBooking.booking_type === 'accommodation_only'
                        ? 'Accommodation Only'
                        : 'Food Only'}
                    </p>
                  </div>
                </div>

                {/* Leader Details */}
                <div className="bg-indigo-50 p-4 rounded-lg border-2 border-indigo-200">
                  <h4 className="font-bold text-indigo-800 mb-2">Team Leader</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{selectedBooking.leader_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{selectedBooking.leader_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold">{selectedBooking.leader_phone}</p>
                    </div>
                  </div>
                </div>

                {/* Members List */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">
                    Members ({selectedBooking.member_count})
                  </h4>
                  <div className="space-y-2">
                    {selectedBooking.members.map((member, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded-lg border">
                        <div className="grid md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Name:</span>{' '}
                            <span className="font-semibold">{member.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>{' '}
                            <span className="font-semibold">{member.email}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>{' '}
                            <span className="font-semibold">{member.phone}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center justify-between p-4 rounded-lg border-2 ${
                  selectedBooking.payment_status === 'completed' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }">
                  <div>
                    <p className="text-sm text-gray-600">Payment Status</p>
                    <p className={`font-bold ${
                      selectedBooking.payment_status === 'completed' 
                        ? 'text-green-700' 
                        : 'text-yellow-700'
                    }`}>
                      {selectedBooking.payment_status === 'completed' ? '✓ Payment Confirmed' : '⏳ Payment Pending'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Validity</p>
                    <p className="font-bold text-gray-800">{selectedBooking.validity_days} Days</p>
                  </div>
                </div>

                {/* Admin Actions */}
                <div className="flex gap-3">
                  {selectedBooking.payment_status === 'pending' ? (
                    <button
                      onClick={() => {
                        handleUpdateBookingStatus(selectedBooking.booking_id, 'completed');
                        setShowBookingDetailsModal(false);
                      }}
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={18} />
                      Mark as Paid
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleUpdateBookingStatus(selectedBooking.booking_id, 'pending');
                        setShowBookingDetailsModal(false);
                      }}
                      className="flex-1 bg-yellow-600 text-white py-3 rounded-lg font-semibold hover:bg-yellow-700 flex items-center justify-center gap-2"
                    >
                      <XCircle size={18} />
                      Mark as Pending
                    </button>
                  )}
                  <button
                    onClick={() => setShowBookingDetailsModal(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <>
            {/* Certificate Actions */}
            <div className="mb-6 flex gap-4">
              <button
                onClick={() => {
                  setShowCertModal(true);
                  setCertMode('single');
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Award size={20} />
                Generate Single Certificate
              </button>
              <button
                onClick={() => {
                  setShowCertModal(true);
                  setCertMode('bulk');
                }}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center gap-2"
              >
                <Upload size={20} />
                Generate Bulk Certificates
              </button>
            </div>

            {/* Certificates List */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-blue-100">
              <div className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white">
                <h2 className="text-2xl font-bold">Generated Certificates</h2>
                <p className="text-sm text-green-100 mt-1">Total: {certificates.length}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Certificate #</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Participant Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Workshop</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Team</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Generated</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {certificates.map((cert) => (
                      <tr key={cert.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">{cert.certificate_number}</td>
                        <td className="px-6 py-4 text-sm text-gray-800">{cert.participant_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{cert.participant_email}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{cert.workshop_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{cert.team_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(cert.generated_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 flex gap-2">
                          <a
                            href={`${BACKEND_URL}/api/certificates/download/${cert.certificate_number}`}
                            download
                            className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-blue-700"
                          >
                            PDF
                          </a>
                          <a
                            href={`${BACKEND_URL}/api/certificates/download-png/${cert.certificate_number}`}
                            download
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-green-700"
                          >
                            PNG
                          </a>
                          <button
                            onClick={() => handleDeleteCertificate(cert.id, cert.certificate_number)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {certificates.length === 0 && (
                  <div className="text-center py-12">
                    <Award className="mx-auto text-gray-300 mb-4" size={64} />
                    <p className="text-gray-500 text-lg">No certificates generated yet</p>
                    <p className="text-gray-400 text-sm mt-2">Click the button above to generate your first certificate</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Certificate Generation Modal */}
        {showCertModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-2xl font-bold text-gray-800">
                  {certMode === 'single' ? 'Generate Single Certificate' : 'Generate Bulk Certificates'}
                </h2>
                <button
                  onClick={() => {
                    setShowCertModal(false);
                    resetCertForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6">
                {certMode === 'single' ? (
                  <form onSubmit={handleGenerateSingleCertificate} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Participant Name *</label>
                        <input
                          type="text"
                          value={certForm.participant_name}
                          onChange={(e) => setCertForm({ ...certForm, participant_name: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Participant Email *</label>
                        <input
                          type="email"
                          value={certForm.participant_email}
                          onChange={(e) => setCertForm({ ...certForm, participant_email: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Workshop Name *</label>
                        <input
                          type="text"
                          value={certForm.workshop_name}
                          onChange={(e) => setCertForm({ ...certForm, workshop_name: e.target.value })}
                          className="form-input"
                          placeholder="e.g., AI Workshop"
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Team Name *</label>
                        <input
                          type="text"
                          value={certForm.team_name}
                          onChange={(e) => setCertForm({ ...certForm, team_name: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Event Dates *</label>
                        <input
                          type="text"
                          value={certForm.event_dates}
                          onChange={(e) => setCertForm({ ...certForm, event_dates: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Event Location *</label>
                        <input
                          type="text"
                          value={certForm.event_location}
                          onChange={(e) => setCertForm({ ...certForm, event_location: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Issue Date *</label>
                      <input
                        type="text"
                        value={certForm.issue_date}
                        onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload Logos & Signatures (Optional)</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="form-label">EvalentX Logo</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setCertLogos({ ...certLogos, codeversity_logo: e.target.files[0] })}
                            className="form-input"
                          />
                        </div>
                        <div>
                          <label className="form-label">Partner Logos (Multiple - Max 5)</label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setCertLogos({ ...certLogos, partner_logos: Array.from(e.target.files).slice(0, 5) })}
                            className="form-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">You can select up to 5 partner logos</p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <label className="form-label">EvalentX CEO Signature</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setCertLogos({ ...certLogos, ceo_signature: e.target.files[0] })}
                              className="form-input"
                            />
                          </div>
                          <div>
                            <label className="form-label">Lead Mentor Signature</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setCertLogos({ ...certLogos, mentor_signature: e.target.files[0] })}
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary flex-1"
                      >
                        {loading ? 'Generating...' : 'Generate Certificate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCertModal(false);
                          resetCertForm();
                        }}
                        className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleGenerateBulkCertificates} className="space-y-4">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">CSV Format Instructions</h4>
                      <p className="text-sm text-blue-800 mb-2">Your CSV file should have the following columns:</p>
                      <code className="text-xs bg-white px-2 py-1 rounded block">
                        Name, Email, Workshop, Team, Date, Location
                      </code>
                      <p className="text-xs text-blue-700 mt-2">Example: John Doe, john@email.com, AI Workshop, Team Alpha, 6th-8th Feb 2026, IIT Gandhinagar</p>
                    </div>

                    <div>
                      <label className="form-label">Upload CSV File *</label>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setBulkCsvFile(e.target.files[0])}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload Logos & Signatures (Optional - Applied to all certificates)</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="form-label">EvalentX Logo</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setCertLogos({ ...certLogos, codeversity_logo: e.target.files[0] })}
                            className="form-input"
                          />
                        </div>
                        <div>
                          <label className="form-label">Partner Logos (Multiple - Max 5)</label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setCertLogos({ ...certLogos, partner_logos: Array.from(e.target.files).slice(0, 5) })}
                            className="form-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">You can select up to 5 partner logos</p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <label className="form-label">EvalentX CEO Signature</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setCertLogos({ ...certLogos, ceo_signature: e.target.files[0] })}
                              className="form-input"
                            />
                          </div>
                          <div>
                            <label className="form-label">Lead Mentor Signature</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setCertLogos({ ...certLogos, mentor_signature: e.target.files[0] })}
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary flex-1"
                      >
                        {loading ? 'Generating Certificates...' : 'Generate All Certificates'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCertModal(false);
                          resetCertForm();
                        }}
                        className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && (
          <div className="space-y-6">
            {/* Broadcast Email Quick Link */}
            <div className="bg-gradient-to-r from-fuchsia-600 to-purple-700 text-white rounded-xl shadow-lg p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-broadcast-link-card">
              <div className="flex items-center gap-3">
                <div className="bg-white/15 p-3 rounded-full">
                  <Send className="text-white" size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Broadcast Email</h3>
                  <p className="text-sm text-white/80">Send announcements to all students, all companies, or selected users.</p>
                </div>
              </div>
              <button
                onClick={() => window.location.assign('/admin/broadcast')}
                className="px-5 py-2.5 rounded-lg bg-white text-purple-700 font-semibold text-sm hover:bg-white/90"
                data-testid="open-broadcast-page-btn"
              >
                Open Broadcast →
              </button>
            </div>

            {/* Email Composer */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Mail className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Send Email to Teams</h2>
                  <p className="text-sm text-gray-600">Compose and send emails to registered teams</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Recipient Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Recipients
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { value: 'all', label: 'All Teams', icon: Users, color: 'blue' },
                      { value: 'paid', label: 'Paid Teams', icon: CheckCircle, color: 'green' },
                      { value: 'unpaid', label: 'Unpaid Teams', icon: Clock, color: 'yellow' },
                      { value: 'selected', label: 'Select Manually', icon: Eye, color: 'purple' }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setEmailRecipientType(option.value)}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          emailRecipientType === option.value
                            ? `border-${option.color}-500 bg-${option.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <option.icon size={24} className={emailRecipientType === option.value ? `text-${option.color}-600` : 'text-gray-400'} />
                        <span className={`text-sm font-medium ${emailRecipientType === option.value ? `text-${option.color}-700` : 'text-gray-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Manual Team Selection */}
                {emailRecipientType === 'selected' && (
                  <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-800">Select Teams ({selectedTeamIds.length} selected)</h4>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={selectAllTeams}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={deselectAllTeams}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {registrations && registrations.map(team => (
                        <label
                          key={team.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            selectedTeamIds.includes(team.id)
                              ? 'bg-purple-100 border-2 border-purple-300'
                              : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTeamIds.includes(team.id)}
                            onChange={() => toggleTeamSelection(team.id)}
                            className="w-5 h-5 text-purple-600 rounded"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{team.team_name}</p>
                            <p className="text-xs text-gray-500">{team.members[0]?.email} • {team.total_members} members</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            team.payment_status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {team.payment_status === 'completed' ? 'Paid' : 'Unpaid'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email Subject */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="e.g., Important Update: Hackathon Schedule"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  />
                </div>

                {/* Email Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Write your message here..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 resize-none"
                    rows={8}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Your message will be formatted with the EvalentX email template
                  </p>
                </div>

                {/* Recipient Count */}
                <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-700">
                        <strong>Estimated recipients:</strong>{' '}
                        {emailRecipientType === 'all' && registrations ? `${registrations.reduce((sum, t) => sum + t.total_members, 0)} participants from ${registrations.length} teams` : ''}
                        {emailRecipientType === 'paid' && registrations ? `${registrations.filter(t => t.payment_status === 'completed').reduce((sum, t) => sum + t.total_members, 0)} participants from ${registrations.filter(t => t.payment_status === 'completed').length} teams` : ''}
                        {emailRecipientType === 'unpaid' && registrations ? `${registrations.filter(t => t.payment_status !== 'completed').reduce((sum, t) => sum + t.total_members, 0)} participants from ${registrations.filter(t => t.payment_status !== 'completed').length} teams` : ''}
                        {emailRecipientType === 'selected' ? `${selectedTeamIds.length} teams selected` : ''}
                      </p>
                    </div>
                    <AlertCircle size={20} className="text-purple-500" />
                  </div>
                </div>

                {/* Send Button */}
                <button
                  onClick={sendBulkEmail}
                  disabled={emailSending || !emailSubject.trim() || !emailMessage.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {emailSending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Sending Emails...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Email History */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gray-100 p-3 rounded-full">
                  <Clock className="text-gray-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Email History</h2>
                  <p className="text-sm text-gray-600">Recent email campaigns sent from this panel</p>
                </div>
              </div>

              {emailLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No emails sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailLogs.map(log => (
                    <div key={log.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{log.subject}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Sent to: {log.recipient_type === 'all' ? 'All Teams' : log.recipient_type === 'paid' ? 'Paid Teams' : log.recipient_type === 'unpaid' ? 'Unpaid Teams' : 'Selected Teams'}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            By {log.sent_by} • {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">
                              ✓ {log.sent_count} sent
                            </span>
                            {log.failed_count > 0 && (
                              <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-semibold">
                                ✗ {log.failed_count} failed
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {log.total_recipients} total recipients
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Result Modal */}
        {showEmailResultModal && emailResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  emailResult.failed_count === 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {emailResult.failed_count === 0 ? (
                    <CheckCircle className="text-green-600" size={32} />
                  ) : (
                    <AlertCircle className="text-yellow-600" size={32} />
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-800">Email Campaign Complete</h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{emailResult.sent_count}</p>
                  <p className="text-sm text-green-700">Sent Successfully</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{emailResult.failed_count}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 text-center mb-6">
                Total recipients: {emailResult.total_recipients}
              </p>

              <button
                onClick={() => setShowEmailResultModal(false)}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Team Project Submissions Tab */}
        {activeTab === 'team-submissions' && (
          <AdminTeamSubmissionsSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Submissions & Jury Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-blue-500">
                <p className="text-gray-600 text-sm">Total Submissions</p>
                <p className="text-3xl font-bold text-blue-600">{submissionStats?.total || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-yellow-500">
                <p className="text-gray-600 text-sm">Pending Review</p>
                <p className="text-3xl font-bold text-yellow-600">{submissionStats?.pending || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-green-500">
                <p className="text-gray-600 text-sm">Qualified</p>
                <p className="text-3xl font-bold text-green-600">{submissionStats?.qualified || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
                <p className="text-gray-600 text-sm">Disqualified</p>
                <p className="text-3xl font-bold text-red-600">{submissionStats?.disqualified || 0}</p>
              </div>
            </div>

            {/* Jury Management Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <Star className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Jury Management</h2>
                    <p className="text-sm text-gray-600">{juryList.length} jury members registered</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowJuryModal(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 flex items-center gap-2"
                  >
                    <UserPlus size={18} />
                    Add Jury
                  </button>
                  <button
                    onClick={handleAutoAssignJury}
                    disabled={assigningJury || juryList.length < 3}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Play size={18} />
                    {assigningJury ? 'Assigning...' : 'Auto-Assign Jury'}
                  </button>
                </div>
              </div>

              {/* Jury List */}
              {juryList.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {juryList.map((jury) => (
                    <div key={jury.id} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <p className="font-bold text-purple-800">{jury.name}</p>
                      <p className="text-sm text-purple-600">@{jury.username}</p>
                      <p className="text-xs text-purple-500 mt-2">
                        Scores Given: {jury.scores_given || 0}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {juryList.length === 0 && (
                <p className="text-center text-gray-500 py-4">No jury members yet. Add at least 3 jury members to start assignments.</p>
              )}
            </div>

            {/* Submissions List */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-800">Project Submissions</h2>
                  <div className="flex gap-2">
                    <select
                      value={submissionStatusFilter}
                      onChange={(e) => setSubmissionStatusFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="qualified">Qualified</option>
                      <option value="disqualified">Disqualified</option>
                    </select>
                    <button
                      onClick={fetchSubmissions}
                      className="bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {getFilteredSubmissions().length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No submissions yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {getFilteredSubmissions().map((sub) => (
                    <div key={sub.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-lg text-gray-800">{sub.project_title}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              sub.status === 'qualified' ? 'bg-green-100 text-green-700' :
                              sub.status === 'disqualified' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {sub.status?.toUpperCase()}
                            </span>
                            {sub.is_on_time && (
                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">On Time</span>
                            )}
                          </div>
                          <p className="text-gray-600 mb-2">Team: <span className="font-semibold">{sub.team_name}</span></p>
                          <p className="text-gray-500 text-sm line-clamp-2 mb-3">{sub.project_description}</p>
                          
                          {/* Tech Stack */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {sub.tech_stack?.map((tech, idx) => (
                              <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                {tech}
                              </span>
                            ))}
                          </div>

                          {/* Links */}
                          <div className="flex gap-4">
                            {sub.github_url && (
                              <a href={sub.github_url} target="_blank" rel="noopener noreferrer" 
                                 className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
                                <Github size={16} /> GitHub
                              </a>
                            )}
                            {sub.demo_url && (
                              <a href={sub.demo_url} target="_blank" rel="noopener noreferrer"
                                 className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
                                <Globe size={16} /> Demo
                              </a>
                            )}
                            {sub.file_uploaded && (
                              <span className="text-gray-500 flex items-center gap-1 text-sm">
                                <Upload size={16} /> File Uploaded
                              </span>
                            )}
                          </div>

                          {/* Scores */}
                          {sub.auto_score > 0 && (
                            <div className="mt-3 flex items-center gap-4">
                              <span className="text-sm text-gray-600">Auto Score: <span className="font-bold">{sub.auto_score}%</span></span>
                              {sub.jury_final_score > 0 && (
                                <span className="text-sm text-purple-600">Jury Score: <span className="font-bold">{sub.jury_final_score}</span></span>
                              )}
                            </div>
                          )}

                          {/* Assigned Jury */}
                          {sub.assigned_jury?.length > 0 && (
                            <div className="mt-3">
                              <span className="text-xs text-gray-500">Assigned Jury: </span>
                              {sub.assigned_jury.map((j, i) => (
                                <span key={i} className={`text-xs px-2 py-1 rounded ml-1 ${
                                  j.scored ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {j.jury_name} {j.scored ? '✓' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          {sub.status === 'pending' && (
                            <button
                              onClick={() => {
                                setSelectedSubmission(sub);
                                setEvaluationForm({
                                  readme_exists: false,
                                  proper_structure: false,
                                  code_runs: false,
                                  tests_pass: false,
                                  on_time: sub.is_on_time || true
                                });
                                setShowEvaluationModal(true);
                              }}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
                            >
                              Evaluate
                            </button>
                          )}
                          {sub.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleManualStatusUpdate(sub.id, 'qualified')}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700"
                              >
                                Qualify
                              </button>
                              <button
                                onClick={() => handleManualStatusUpdate(sub.id, 'disqualified')}
                                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700"
                              >
                                Disqualify
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Evaluation Modal */}
        {showEvaluationModal && selectedSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-2xl">
                <h2 className="text-xl font-bold">Evaluate Submission</h2>
                <p className="text-blue-200">{selectedSubmission.project_title}</p>
                <p className="text-blue-300 text-sm">Team: {selectedSubmission.team_name}</p>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Check the criteria that the submission meets. A score of 60% or higher qualifies for jury review.
                </p>

                {[
                  { key: 'readme_exists', label: 'README Documentation', desc: 'Has proper README with instructions (20%)' },
                  { key: 'proper_structure', label: 'Code Structure', desc: 'Well-organized project structure (20%)' },
                  { key: 'code_runs', label: 'Code Runs', desc: 'Application builds and runs successfully (25%)' },
                  { key: 'tests_pass', label: 'Tests Pass', desc: 'Has tests and they pass (15%)' },
                  { key: 'on_time', label: 'Submitted On Time', desc: 'Submitted before deadline (20%)' }
                ].map(({ key, label, desc }) => (
                  <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={evaluationForm[key]}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, [key]: e.target.checked })}
                      className="w-5 h-5 mt-0.5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-semibold text-gray-800">{label}</p>
                      <p className="text-sm text-gray-500">{desc}</p>
                    </div>
                  </label>
                ))}

                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 font-semibold">
                    Current Score: {
                      (evaluationForm.readme_exists ? 20 : 0) +
                      (evaluationForm.proper_structure ? 20 : 0) +
                      (evaluationForm.code_runs ? 25 : 0) +
                      (evaluationForm.tests_pass ? 15 : 0) +
                      (evaluationForm.on_time ? 20 : 0)
                    }%
                  </p>
                  <p className="text-blue-600 text-sm">
                    {(evaluationForm.readme_exists ? 20 : 0) +
                     (evaluationForm.proper_structure ? 20 : 0) +
                     (evaluationForm.code_runs ? 25 : 0) +
                     (evaluationForm.tests_pass ? 15 : 0) +
                     (evaluationForm.on_time ? 20 : 0) >= 60
                      ? '✓ Will qualify for jury review'
                      : '✗ Will not qualify (need 60%+)'}
                  </p>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEvaluationModal(false);
                      setSelectedSubmission(null);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEvaluateSubmission}
                    disabled={evaluating}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {evaluating ? 'Evaluating...' : 'Submit Evaluation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Jury Modal */}
        {showJuryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full">
              <div className="p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-2xl">
                <h2 className="text-xl font-bold">Create Jury Account</h2>
                <p className="text-purple-200 text-sm">Add a new jury member for evaluation</p>
              </div>

              <form onSubmit={handleCreateJury} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={juryForm.name}
                    onChange={(e) => setJuryForm({ ...juryForm, name: e.target.value })}
                    placeholder="Dr. John Doe"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                  <input
                    type="text"
                    value={juryForm.username}
                    onChange={(e) => setJuryForm({ ...juryForm, username: e.target.value })}
                    placeholder="jury_johndoe"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={juryForm.password}
                    onChange={(e) => setJuryForm({ ...juryForm, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    required
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowJuryModal(false);
                      setJuryForm({ username: '', password: '', name: '' });
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingJury}
                    className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
                  >
                    {creatingJury ? 'Creating...' : 'Create Jury'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <AdminReferralsSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Messages Tab (Contact Us & Investor Corner) */}
        {activeTab === 'messages' && (
          <AdminSubmissionsSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Problem Statements Tab */}
        {activeTab === 'problem-statements' && (
          <AdminProblemStatementsSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Jury Management Tab */}
        {activeTab === 'jury-management' && (
          <AdminJuryManagementSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Round Configuration Tab */}
        {activeTab === 'round-config' && (
          <AdminRoundConfigSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Jury Assignments Tab */}
        {activeTab === 'jury-assignments' && (
          <AdminJuryAssignmentSection token={localStorage.getItem('admin_token')} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Notification Banner Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 p-3 rounded-full">
                  <Bell className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Notification Banner</h2>
                  <p className="text-sm text-gray-600">Edit the notification message displayed across the platform</p>
                </div>
              </div>

              {/* Preview */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">Live Preview:</p>
                <div className={`bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg ${!notificationBanner.is_active ? 'opacity-50' : ''}`}>
                  <p className="text-sm font-semibold text-center">
                    {notificationBanner.text || "Enter your notification text..."}
                  </p>
                </div>
                {!notificationBanner.is_active && (
                  <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                    <XCircle size={14} /> Banner is currently hidden from users
                  </p>
                )}
              </div>

              {/* Editor */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Banner Text
                  </label>
                  <textarea
                    value={notificationBanner.text}
                    onChange={(e) => setNotificationBanner({ ...notificationBanner, text: e.target.value })}
                    placeholder="e.g., 🎉 Early bird registrations open until 31st December 2025"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                    rows={3}
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    {notificationBanner.text?.length || 0} / 500 characters
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationBanner.is_active}
                      onChange={(e) => setNotificationBanner({ ...notificationBanner, is_active: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm font-medium text-gray-700">
                    {notificationBanner.is_active ? 'Banner is visible to users' : 'Banner is hidden'}
                  </span>
                </div>

                {/* Last Updated Info */}
                {notificationBanner.updated_at && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                    Last updated: {new Date(notificationBanner.updated_at).toLocaleString()}
                    {notificationBanner.updated_by && ` by ${notificationBanner.updated_by}`}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={updateNotificationBanner}
                    disabled={bannerLoading}
                    className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                      bannerSaved 
                        ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    } disabled:opacity-50`}
                  >
                    {bannerLoading ? (
                      'Saving...'
                    ) : bannerSaved ? (
                      <>
                        <CheckCircle size={20} />
                        Saved!
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Edition-Based Notification Bar Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Layers className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Edition Notification Bars</h2>
                  <p className="text-sm text-gray-600">Control notification banners for each program edition</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Hackathon Edition */}
                <div className="border-2 border-purple-200 rounded-xl p-5 bg-purple-50/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                        <Trophy size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">Hackathon Edition</h3>
                        <p className="text-xs text-gray-500">EvalentX 2nd Edition</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editionBars.hackathon?.enabled || false}
                        onChange={(e) => updateEditionBarField('hackathon', 'enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {/* Live Preview */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Preview:</p>
                    <div 
                      className={`py-2.5 px-4 rounded-lg text-white text-sm font-medium text-center cursor-pointer transition-all ${!editionBars.hackathon?.enabled ? 'opacity-40' : ''}`}
                      style={{ backgroundColor: editionBars.hackathon?.background_color || '#7c3aed' }}
                    >
                      {editionBars.hackathon?.text || 'Enter notification text...'}
                      {editionBars.hackathon?.redirect_link && (
                        <span className="ml-2 text-xs opacity-75">→ {editionBars.hackathon.redirect_link}</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Notification Text</label>
                      <textarea
                        value={editionBars.hackathon?.text || ''}
                        onChange={(e) => updateEditionBarField('hackathon', 'text', e.target.value)}
                        placeholder="e.g., 🚀 Registrations open for 2nd Edition!"
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 resize-none text-sm"
                        rows={2}
                        maxLength={500}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Redirect Link (optional)</label>
                        <input
                          type="text"
                          value={editionBars.hackathon?.redirect_link || ''}
                          onChange={(e) => updateEditionBarField('hackathon', 'redirect_link', e.target.value)}
                          placeholder="/register"
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Background Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editionBars.hackathon?.background_color || '#7c3aed'}
                            onChange={(e) => updateEditionBarField('hackathon', 'background_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={editionBars.hackathon?.background_color || '#7c3aed'}
                            onChange={(e) => updateEditionBarField('hackathon', 'background_color', e.target.value)}
                            placeholder="#7c3aed"
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Expiry Date (optional)</label>
                      <input
                        type="datetime-local"
                        value={editionBars.hackathon?.expiry_date?.slice(0, 16) || ''}
                        onChange={(e) => updateEditionBarField('hackathon', 'expiry_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                    </div>

                    {editionBars.hackathon?.updated_at && (
                      <p className="text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
                        Last updated: {new Date(editionBars.hackathon.updated_at).toLocaleString()}
                        {editionBars.hackathon.updated_by && ` by ${editionBars.hackathon.updated_by}`}
                      </p>
                    )}

                    <button
                      onClick={() => updateEditionBar('hackathon')}
                      disabled={editionBarLoading.hackathon}
                      className={`w-full py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm ${
                        editionBarSaved.hackathon 
                          ? 'bg-green-600 text-white' 
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      } disabled:opacity-50`}
                    >
                      {editionBarLoading.hackathon ? 'Saving...' : editionBarSaved.hackathon ? (
                        <><CheckCircle size={18} /> Saved!</>
                      ) : (
                        <><Save size={18} /> Save Hackathon Banner</>
                      )}
                    </button>
                  </div>
                </div>

                {/* AI League Edition */}
                <div className="border-2 border-cyan-200 rounded-xl p-5 bg-cyan-50/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                        <Brain size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">AI League Edition</h3>
                        <p className="text-xs text-gray-500">India's AI Premium League</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editionBars.ai_league?.enabled || false}
                        onChange={(e) => updateEditionBarField('ai_league', 'enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>

                  {/* Live Preview */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Preview:</p>
                    <div 
                      className={`py-2.5 px-4 rounded-lg text-white text-sm font-medium text-center cursor-pointer transition-all ${!editionBars.ai_league?.enabled ? 'opacity-40' : ''}`}
                      style={{ backgroundColor: editionBars.ai_league?.background_color || '#0891b2' }}
                    >
                      {editionBars.ai_league?.text || 'Enter notification text...'}
                      {editionBars.ai_league?.redirect_link && (
                        <span className="ml-2 text-xs opacity-75">→ {editionBars.ai_league.redirect_link}</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Notification Text</label>
                      <textarea
                        value={editionBars.ai_league?.text || ''}
                        onChange={(e) => updateEditionBarField('ai_league', 'text', e.target.value)}
                        placeholder="e.g., 🧠 Join India's AI Premium League!"
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 resize-none text-sm"
                        rows={2}
                        maxLength={500}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Redirect Link (optional)</label>
                        <input
                          type="text"
                          value={editionBars.ai_league?.redirect_link || ''}
                          onChange={(e) => updateEditionBarField('ai_league', 'redirect_link', e.target.value)}
                          placeholder="/ai-league"
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Background Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editionBars.ai_league?.background_color || '#0891b2'}
                            onChange={(e) => updateEditionBarField('ai_league', 'background_color', e.target.value)}
                            className="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={editionBars.ai_league?.background_color || '#0891b2'}
                            onChange={(e) => updateEditionBarField('ai_league', 'background_color', e.target.value)}
                            placeholder="#0891b2"
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Expiry Date (optional)</label>
                      <input
                        type="datetime-local"
                        value={editionBars.ai_league?.expiry_date?.slice(0, 16) || ''}
                        onChange={(e) => updateEditionBarField('ai_league', 'expiry_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                    </div>

                    {editionBars.ai_league?.updated_at && (
                      <p className="text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
                        Last updated: {new Date(editionBars.ai_league.updated_at).toLocaleString()}
                        {editionBars.ai_league.updated_by && ` by ${editionBars.ai_league.updated_by}`}
                      </p>
                    )}

                    <button
                      onClick={() => updateEditionBar('ai_league')}
                      disabled={editionBarLoading.ai_league}
                      className={`w-full py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm ${
                        editionBarSaved.ai_league 
                          ? 'bg-green-600 text-white' 
                          : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                      } disabled:opacity-50`}
                    >
                      {editionBarLoading.ai_league ? 'Saving...' : editionBarSaved.ai_league ? (
                        <><CheckCircle size={18} /> Saved!</>
                      ) : (
                        <><Save size={18} /> Save AI League Banner</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Submission Visibility Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-full">
                  <Code className="text-green-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Project Submission</h2>
                  <p className="text-sm text-gray-600">Control when teams can submit their projects</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {submissionEnabled ? '✅ Submissions are OPEN' : '🔒 Submissions are CLOSED'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {submissionEnabled 
                        ? 'Paid teams can see and use the "Submit Your Project" button on their dashboard' 
                        : 'The "Submit Your Project" button is hidden from all team dashboards'}
                    </p>
                  </div>
                  <button
                    onClick={toggleSubmissionEnabled}
                    disabled={submissionEnabledLoading}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                      submissionEnabled 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    } disabled:opacity-50`}
                  >
                    {submissionEnabledLoading ? 'Updating...' : (
                      submissionEnabled ? 'Disable Submissions' : 'Enable Submissions'
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="inline mr-2" size={16} />
                  <strong>Note:</strong> Only paid teams can submit projects. Enable submissions when you&apos;re ready to start accepting project submissions from teams.
                </p>
              </div>
            </div>

            {/* Pricing Management Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-indigo-100 p-3 rounded-full">
                  <DollarSign className="text-indigo-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Pricing Management</h2>
                  <p className="text-sm text-gray-600">Manage all event-related fees from one place</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Hackathon Entry Fee */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hackathon Entry Fee (per person)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={pricing.hackathon_entry_fee}
                      onChange={(e) => setPricing({ ...pricing, hackathon_entry_fee: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Registration fee for hackathon participation</p>
                </div>

                {/* Accommodation with Food */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Accommodation + Food (per person)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={pricing.accommodation_with_food}
                      onChange={(e) => setPricing({ ...pricing, accommodation_with_food: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">3-day stay with all meals included</p>
                </div>

                {/* Accommodation Only */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Accommodation Only (per person)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={pricing.accommodation_only}
                      onChange={(e) => setPricing({ ...pricing, accommodation_only: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">3-day stay without meals</p>
                </div>

                {/* Food Only */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Food Only (per person)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={pricing.food_only}
                      onChange={(e) => setPricing({ ...pricing, food_only: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">All meals for the event duration</p>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="mt-6 bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <h4 className="font-semibold text-indigo-800 mb-3">Current Pricing Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-indigo-600">Entry Fee</p>
                    <p className="text-lg font-bold text-indigo-800">₹{pricing.hackathon_entry_fee}</p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600">Accom + Food</p>
                    <p className="text-lg font-bold text-indigo-800">₹{pricing.accommodation_with_food}</p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600">Accom Only</p>
                    <p className="text-lg font-bold text-indigo-800">₹{pricing.accommodation_only}</p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600">Food Only</p>
                    <p className="text-lg font-bold text-indigo-800">₹{pricing.food_only}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={updatePricing}
                  disabled={pricingLoading}
                  className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                    pricingSaved 
                      ? 'bg-green-600 text-white' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  } disabled:opacity-50`}
                >
                  {pricingLoading ? (
                    'Saving...'
                  ) : pricingSaved ? (
                    <>
                      <CheckCircle size={20} />
                      Pricing Saved!
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Save Pricing
                    </>
                  )}
                </button>
                <button
                  onClick={applyPricingToUnpaid}
                  disabled={applyingPricing}
                  className="px-6 py-3 rounded-lg font-semibold bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2 disabled:opacity-50"
                >
                  {applyingPricing ? 'Applying...' : 'Apply to Unpaid Teams'}
                </button>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="inline mr-2" size={16} />
                  <strong>Note:</strong> Price changes apply immediately to new registrations. Click &quot;Apply to Unpaid Teams&quot; to update pricing for existing unpaid registrations. Paid registrations retain their original pricing.
                </p>
              </div>
            </div>

            {/* Date-Based Booking Pricing */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-teal-100 p-3 rounded-full">
                  <Calendar className="text-teal-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Date-Based Booking Pricing</h2>
                  <p className="text-sm text-gray-600">Set pricing based on number of days selected (Feb 6-8, 2026)</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Accommodation + Food */}
                <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <h4 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
                    <Home size={18} /> Accommodation + Food
                  </h4>
                  <div className="mb-3 pb-3 border-b border-teal-200">
                    <label className="text-xs text-teal-600 block font-bold">Per Day Rate (Primary)</label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">₹</span>
                      <input
                        type="number"
                        value={datePricing.accommodation_food?.per_day || datePricing.accommodation_food?.['1_day'] || 0}
                        onChange={(e) => {
                          const perDayVal = parseInt(e.target.value) || 0;
                          setDatePricing({
                            ...datePricing,
                            accommodation_food: { 
                              ...datePricing.accommodation_food, 
                              per_day: perDayVal,
                              '1_day': perDayVal,
                              '2_days': perDayVal * 2,
                              '3_days': perDayVal * 3
                            }
                          });
                        }}
                        className="w-full px-2 py-1 border-2 border-teal-400 rounded text-sm font-bold"
                      />
                    </div>
                    <p className="text-xs text-teal-500 mt-1">Auto-calculates multi-day pricing</p>
                  </div>
                  {['1_day', '2_days', '3_days'].map((key) => (
                    <div key={key} className="mb-2">
                      <label className="text-xs text-teal-600 block">{key.replace('_', ' ').replace('day', 'Day')}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">₹</span>
                        <input
                          type="number"
                          value={datePricing.accommodation_food?.[key] || 0}
                          onChange={(e) => setDatePricing({
                            ...datePricing,
                            accommodation_food: { ...datePricing.accommodation_food, [key]: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full px-2 py-1 border rounded text-sm bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Accommodation Only */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Home size={18} /> Accommodation Only
                  </h4>
                  <div className="mb-3 pb-3 border-b border-blue-200">
                    <label className="text-xs text-blue-600 block font-bold">Per Day Rate (Primary)</label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">₹</span>
                      <input
                        type="number"
                        value={datePricing.accommodation_only?.per_day || datePricing.accommodation_only?.['1_day'] || 0}
                        onChange={(e) => {
                          const perDayVal = parseInt(e.target.value) || 0;
                          setDatePricing({
                            ...datePricing,
                            accommodation_only: { 
                              ...datePricing.accommodation_only, 
                              per_day: perDayVal,
                              '1_day': perDayVal,
                              '2_days': perDayVal * 2,
                              '3_days': perDayVal * 3
                            }
                          });
                        }}
                        className="w-full px-2 py-1 border-2 border-blue-400 rounded text-sm font-bold"
                      />
                    </div>
                    <p className="text-xs text-blue-500 mt-1">Auto-calculates multi-day pricing</p>
                  </div>
                  {['1_day', '2_days', '3_days'].map((key) => (
                    <div key={key} className="mb-2">
                      <label className="text-xs text-blue-600 block">{key.replace('_', ' ').replace('day', 'Day')}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">₹</span>
                        <input
                          type="number"
                          value={datePricing.accommodation_only?.[key] || 0}
                          onChange={(e) => setDatePricing({
                            ...datePricing,
                            accommodation_only: { ...datePricing.accommodation_only, [key]: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full px-2 py-1 border rounded text-sm bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Food Only */}
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                    <Utensils size={18} /> Food Only
                  </h4>
                  <div className="mb-3 pb-3 border-b border-orange-200">
                    <label className="text-xs text-orange-600 block font-bold">Per Day Rate (Primary)</label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">₹</span>
                      <input
                        type="number"
                        value={datePricing.food_only?.per_day || datePricing.food_only?.['1_day'] || 0}
                        onChange={(e) => {
                          const perDayVal = parseInt(e.target.value) || 0;
                          setDatePricing({
                            ...datePricing,
                            food_only: { 
                              ...datePricing.food_only, 
                              per_day: perDayVal,
                              '1_day': perDayVal,
                              '2_days': perDayVal * 2,
                              '3_days': perDayVal * 3
                            }
                          });
                        }}
                        className="w-full px-2 py-1 border-2 border-orange-400 rounded text-sm font-bold"
                      />
                    </div>
                    <p className="text-xs text-orange-500 mt-1">Auto-calculates multi-day pricing</p>
                  </div>
                  {['1_day', '2_days', '3_days'].map((key) => (
                    <div key={key} className="mb-2">
                      <label className="text-xs text-orange-600 block">{key.replace('_', ' ').replace('day', 'Day')}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">₹</span>
                        <input
                          type="number"
                          value={datePricing.food_only?.[key] || 0}
                          onChange={(e) => setDatePricing({
                            ...datePricing,
                            food_only: { ...datePricing.food_only, [key]: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full px-2 py-1 border rounded text-sm bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={updateDatePricing}
                disabled={datePricingLoading}
                className={`mt-4 w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  datePricingSaved ? 'bg-green-600 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'
                } disabled:opacity-50`}
              >
                {datePricingLoading ? 'Saving...' : datePricingSaved ? (
                  <><CheckCircle size={20} /> Date Pricing Saved!</>
                ) : (
                  <><Save size={20} /> Save Date-Based Pricing</>
                )}
              </button>
            </div>

            {/* Resume System Pricing */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-3 rounded-full">
                  <FileText className="text-orange-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Resume System Pricing</h2>
                  <p className="text-sm text-gray-600">Configure pricing for Verified Developer Resume features</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Premium Resume Price */}
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <label className="block text-sm font-semibold text-orange-800 mb-2">
                    Premium Resume Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={resumePricing.premium_price}
                      onChange={(e) => setResumePricing({ ...resumePricing, premium_price: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-orange-300 rounded-lg focus:border-orange-500"
                      min="0"
                      data-testid="resume-premium-price-input"
                    />
                  </div>
                  <p className="text-xs text-orange-600 mt-1">Includes: Verified badge, PDF download, analytics, priority in search</p>
                </div>

                {/* AI Optimization Price */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <label className="block text-sm font-semibold text-purple-800 mb-2">
                    AI Optimization Credits Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">₹</span>
                    <input
                      type="number"
                      value={resumePricing.ai_optimization_price}
                      onChange={(e) => setResumePricing({ ...resumePricing, ai_optimization_price: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 border-2 border-purple-300 rounded-lg focus:border-purple-500"
                      min="0"
                      data-testid="resume-ai-price-input"
                    />
                  </div>
                  <p className="text-xs text-purple-600 mt-1">Price for 5 AI generation credits (Athena AI powered)</p>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="mt-6 bg-gradient-to-r from-orange-100 to-purple-100 rounded-lg p-4 border border-orange-200">
                <h4 className="font-semibold text-gray-800 mb-3">Current Resume Pricing</h4>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-xs text-orange-600">Premium Resume</p>
                    <p className="text-2xl font-bold text-orange-800">₹{resumePricing.premium_price}</p>
                    <p className="text-xs text-gray-500">One-time payment</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600">AI Credits (5x)</p>
                    <p className="text-2xl font-bold text-purple-800">₹{resumePricing.ai_optimization_price}</p>
                    <p className="text-xs text-gray-500">Per 5 credits</p>
                  </div>
                </div>
              </div>

              <button
                onClick={updateResumePricing}
                disabled={resumePricingLoading}
                className={`mt-4 w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  resumePricingSaved ? 'bg-green-600 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'
                } disabled:opacity-50`}
                data-testid="save-resume-pricing-btn"
              >
                {resumePricingLoading ? 'Saving...' : resumePricingSaved ? (
                  <><CheckCircle size={20} /> Resume Pricing Saved!</>
                ) : (
                  <><Save size={20} /> Save Resume Pricing</>
                )}
              </button>
            </div>

            {/* Coupon Management */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <Tag className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Coupon Codes</h2>
                    <p className="text-sm text-gray-600">{coupons.length} coupons created</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    resetCouponForm();
                    setEditingCoupon(null);
                    setShowCouponModal(true);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 flex items-center gap-2"
                >
                  <Plus size={18} /> Create Coupon
                </button>
              </div>

              {coupons.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No coupons created yet</p>
              ) : (
                <div className="space-y-3">
                  {coupons.map((coupon) => (
                    <div key={coupon.id} className={`border rounded-lg p-4 ${coupon.is_active ? 'bg-white border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-bold text-lg text-purple-700">{coupon.code}</span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `₹${coupon.discount_value} off`} 
                            {' • '} Used: {coupon.current_uses || 0}{coupon.max_uses ? `/${coupon.max_uses}` : ''}
                            {coupon.valid_until && ` • Expires: ${new Date(coupon.valid_until).toLocaleDateString()}`}
                          </p>
                          {coupon.description && <p className="text-xs text-gray-500 mt-1">{coupon.description}</p>}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleCouponStatus(coupon.id)}
                            className={`p-2 rounded-lg ${coupon.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                          >
                            {coupon.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </button>
                          <button
                            onClick={() => {
                              setEditingCoupon(coupon);
                              setCouponForm({
                                code: coupon.code,
                                discount_type: coupon.discount_type,
                                discount_value: coupon.discount_value,
                                applicable_to: coupon.applicable_to || ['hackathon_entry_fee'],
                                max_uses: coupon.max_uses,
                                max_uses_per_user: coupon.max_uses_per_user || 1,
                                valid_from: coupon.valid_from || '',
                                valid_until: coupon.valid_until || '',
                                is_active: coupon.is_active,
                                description: coupon.description || ''
                              });
                              setShowCouponModal(true);
                            }}
                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                          >
                            <Edit size={20} />
                          </button>
                          <button
                            onClick={() => deleteCoupon(coupon.id)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Referral Codes */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-pink-100 p-3 rounded-full">
                  <Gift className="text-pink-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Referral Codes</h2>
                  <p className="text-sm text-gray-600">{referrals.length} referral codes generated by users</p>
                </div>
              </div>

              {referrals.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No referral codes generated yet</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {referrals.map((ref) => (
                    <div key={ref.id} className={`border rounded-lg p-4 ${ref.is_active ? 'bg-white border-pink-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-bold text-pink-700">{ref.code}</span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${ref.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {ref.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            By: {ref.referrer_username} • {ref.discount_value}% off • Used: {ref.current_uses || 0}
                          </p>
                          <p className="text-xs text-gray-500">Credits earned: ₹{ref.total_credits_earned || 0}</p>
                        </div>
                        <button
                          onClick={() => toggleReferralStatus(ref.id)}
                          className={`p-2 rounded-lg ${ref.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                        >
                          {ref.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Registration Control Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 p-3 rounded-full">
                  <Lock className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Registration Control</h2>
                  <p className="text-sm text-gray-600">Enable or disable registrations for different passes</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {/* Hackathon Registration */}
                <div className={`p-4 rounded-xl border-2 ${registrationSettings.hackathon_registration ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Users size={20} className={registrationSettings.hackathon_registration ? 'text-green-600' : 'text-red-600'} />
                      <span className="font-semibold text-gray-800">Hackathon</span>
                    </div>
                    <button
                      onClick={() => toggleRegistrationSetting('hackathon_registration')}
                      disabled={registrationSettingsLoading}
                      className={`p-2 rounded-lg transition-colors ${registrationSettings.hackathon_registration ? 'text-green-600 hover:bg-green-100' : 'text-red-600 hover:bg-red-100'}`}
                    >
                      {registrationSettings.hackathon_registration ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">Team registration for hackathon</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${registrationSettings.hackathon_registration ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>
                    {registrationSettings.hackathon_registration ? 'Open' : 'Closed'}
                  </span>
                </div>

                {/* Accommodation Booking */}
                <div className={`p-4 rounded-xl border-2 ${registrationSettings.accommodation_booking ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Home size={20} className={registrationSettings.accommodation_booking ? 'text-green-600' : 'text-red-600'} />
                      <span className="font-semibold text-gray-800">Accommodation</span>
                    </div>
                    <button
                      onClick={() => toggleRegistrationSetting('accommodation_booking')}
                      disabled={registrationSettingsLoading}
                      className={`p-2 rounded-lg transition-colors ${registrationSettings.accommodation_booking ? 'text-green-600 hover:bg-green-100' : 'text-red-600 hover:bg-red-100'}`}
                    >
                      {registrationSettings.accommodation_booking ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">Accommodation pass booking</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${registrationSettings.accommodation_booking ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>
                    {registrationSettings.accommodation_booking ? 'Open' : 'Closed'}
                  </span>
                </div>

                {/* Food Booking */}
                <div className={`p-4 rounded-xl border-2 ${registrationSettings.food_booking ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Utensils size={20} className={registrationSettings.food_booking ? 'text-green-600' : 'text-red-600'} />
                      <span className="font-semibold text-gray-800">Food Pass</span>
                    </div>
                    <button
                      onClick={() => toggleRegistrationSetting('food_booking')}
                      disabled={registrationSettingsLoading}
                      className={`p-2 rounded-lg transition-colors ${registrationSettings.food_booking ? 'text-green-600 hover:bg-green-100' : 'text-red-600 hover:bg-red-100'}`}
                    >
                      {registrationSettings.food_booking ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">Food only pass booking</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${registrationSettings.food_booking ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>
                    {registrationSettings.food_booking ? 'Open' : 'Closed'}
                  </span>
                </div>
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="inline mr-2" size={16} />
                  <strong>Note:</strong> Disabling registration will prevent new bookings. Existing bookings and payments will not be affected.
                </p>
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <h3 className="font-bold text-blue-800 mb-3">💡 Tips for effective banners</h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• Keep the message concise and clear (under 100 characters recommended)</li>
                <li>• Use emojis to grab attention (🎉, 🚀, ⚡, 📢)</li>
                <li>• Include important dates or deadlines</li>
                <li>• Update regularly to keep content fresh</li>
              </ul>
            </div>
          </div>
        )}

        {/* Super Admin Configuration Tab */}
        {activeTab === 'super-config' && (
          <SuperAdminConfigPanel token={localStorage.getItem('admin_token')} />
        )}

        {/* Team Registry Tab */}
        {activeTab === 'team-registry' && (
          <TeamRegistryAdmin />
        )}

        {/* POTD Management Tab */}
        {activeTab === 'potd-management' && (
          <POTDManagementAdmin />
        )}

        {/* Investor Connect Tab */}
        {activeTab === 'investor-connect' && (
          <InvestorConnectAdmin />
        )}

        {/* Event Hosting Tab */}
        {activeTab === 'event-hosting' && (
          <AdminEventsSection />
        )}

        {/* Super Admin Panel */}
        {activeTab === 'super-admin' && (
          <SuperAdminPanel token={localStorage.getItem('admin_token')} />
        )}

        {/* Company Assessment Portal Tab */}
        {activeTab === 'company-assessments' && (
          <CompanyAssessmentAdmin />
        )}

        {/* EvalentX Arena Students Tab */}
        {activeTab === 'codearena-students' && (
          <CodeArenaStudentsAdmin />
        )}

        {/* EvalentX Arena Dashboard Tab */}
        {activeTab === 'codearena-dashboard' && (
          <CodeArenaDashboardAdmin />
        )}

        {/* EvalentX Arena Contests Tab */}
        {activeTab === 'codearena-contests' && (
          <CodeArenaContestAdmin />
        )}

        {/* EvalentX Arena Certificates Tab */}
        {activeTab === 'codearena-certificates' && (
          <CodeArenaCertificatesAdmin />
        )}

        {/* Job Approvals Tab */}
        {activeTab === 'job-approvals' && (
          <div className="space-y-6">
            {/* Header & Filter */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-800">Job Postings Review</h3>
                  <select
                    value={jobStatusFilter}
                    onChange={(e) => {
                      setJobStatusFilter(e.target.value);
                      fetchJobApprovals(e.target.value);
                    }}
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-rose-500"
                    data-testid="job-status-filter"
                  >
                    <option value="pending">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="deleted">Deleted</option>
                    <option value="all">All Jobs</option>
                  </select>
                </div>
                <button
                  onClick={() => fetchJobApprovals(jobStatusFilter)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <RefreshCw size={18} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-amber-500">
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{pendingJobs.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Approved</p>
                <p className="text-2xl font-bold text-green-600">{allJobs.filter(j => j.approval_status === 'approved').length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-red-500">
                <p className="text-gray-500 text-sm">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{allJobs.filter(j => j.approval_status === 'rejected').length}</p>
              </div>
            </div>

            {/* Jobs Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Job Title</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {jobsLoading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          <Loader className="animate-spin mx-auto mb-2" size={24} />
                          Loading...
                        </td>
                      </tr>
                    ) : allJobs.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No job postings found
                        </td>
                      </tr>
                    ) : (
                      allJobs.map((job) => {
                        const isDeleted = job.is_deleted || job.approval_status === 'deleted';
                        const currentStatus = isDeleted ? 'deleted' : (job.approval_status || (job.is_active ? 'approved' : 'pending'));
                        return (
                        <tr key={job.id} className="hover:bg-gray-50" data-testid={`job-row-${job.id}`}>
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900">{job.title}</div>
                            <div className="text-xs text-gray-500">{job.id}</div>
                          </td>
                          <td className="px-4 py-4 text-gray-700">{job.company_name || 'N/A'}</td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              job.job_type === 'internship' ? 'bg-blue-100 text-blue-700' :
                              job.job_type === 'full-time' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {job.job_type || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-700">{job.location || 'Remote'}</td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              currentStatus === 'approved' ? 'bg-green-100 text-green-700' :
                              currentStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                              currentStatus === 'deleted' ? 'bg-gray-200 text-gray-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {currentStatus}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-500 text-sm">
                            {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              {isDeleted ? (
                                <>
                                  <button
                                    onClick={() => handleRestoreJob(job.id)}
                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                                    data-testid={`restore-job-${job.id}`}
                                  >
                                    Restore
                                  </button>
                                  <button
                                    onClick={() => handlePurgeJob(job.id)}
                                    className="px-3 py-1 bg-red-700 text-white text-xs rounded-lg hover:bg-red-800"
                                    data-testid={`purge-job-${job.id}`}
                                  >
                                    Purge
                                  </button>
                                </>
                              ) : (
                                <>
                                  {currentStatus !== 'approved' && (
                                    <button
                                      onClick={() => handleApproveJob(job.id)}
                                      className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                                      data-testid={`approve-job-${job.id}`}
                                    >
                                      Approve
                                    </button>
                                  )}
                                  {currentStatus !== 'rejected' && (
                                    <button
                                      onClick={() => handleRejectJob(job.id)}
                                      className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700"
                                      data-testid={`reject-job-${job.id}`}
                                    >
                                      Reject
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteJob(job.id)}
                                    className="px-3 py-1 bg-gray-500 text-white text-xs rounded-lg hover:bg-gray-600"
                                    data-testid={`delete-job-${job.id}`}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise Hosting Tab */}
        {activeTab === 'enterprise-hosting' && (
          <div className="space-y-6">
            {/* Analytics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Total Challenges</p>
                <p className="text-2xl font-bold text-purple-600">{enterpriseAnalytics?.total_challenges || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-amber-500">
                <p className="text-gray-500 text-sm">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{enterpriseAnalytics?.status_breakdown?.pending_approval || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-cyan-500">
                <p className="text-gray-500 text-sm">Total Registrations</p>
                <p className="text-2xl font-bold text-cyan-600">{enterpriseAnalytics?.total_registrations || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">₹{(enterpriseAnalytics?.total_revenue || 0).toLocaleString()}</p>
              </div>
            </div>

            {/* Sub-Tabs for Challenges, Pricing, Coupons */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="flex gap-2 border-b border-gray-200 pb-3 mb-4">
                {[
                  { id: 'challenges', label: 'Challenges', icon: Trophy },
                  { id: 'pricing', label: 'Pricing Plans', icon: DollarSign },
                  { id: 'coupons', label: 'Coupons', icon: Tag }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setEnterpriseActiveSubTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                      enterpriseActiveSubTab === tab.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Challenges Sub-Tab */}
              {enterpriseActiveSubTab === 'challenges' && (
                <>
                  {/* Status Filters */}
                  <div className="flex gap-2 mb-4">
                    {['pending', 'approved', 'live', 'all'].map(filter => (
                      <button
                        key={filter}
                        onClick={() => {
                          setEnterpriseFilter(filter);
                          fetchEnterpriseData(filter);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                          enterpriseFilter === filter
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {filter === 'pending' ? 'Pending Approval' : filter}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Pricing Sub-Tab */}
              {enterpriseActiveSubTab === 'pricing' && (
                <div className="mb-4">
                  <p className="text-gray-500 text-sm mb-2">Configure pricing plans for Enterprise Challenge Hosting. Click on a plan to edit.</p>
                </div>
              )}

              {/* Coupons Sub-Tab */}
              {enterpriseActiveSubTab === 'coupons' && (
                <div className="flex justify-between items-center mb-4">
                  <p className="text-gray-500 text-sm">Manage discount coupons for Enterprise Challenge Hosting.</p>
                  <button
                    onClick={() => {
                      setEditingEnterpriseCoupon(null);
                      setEnterpriseCouponForm({
                        code: '', discount_type: 'percentage', discount_value: 10, applicable_plans: ['starter', 'pro', 'enterprise'],
                        max_uses: 100, used_count: 0, valid_from: '', valid_until: '', is_active: true
                      });
                      setShowEnterpriseCouponModal(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Create Coupon
                  </button>
                </div>
              )}
            </div>

            {/* Challenges List */}
            {enterpriseActiveSubTab === 'challenges' && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Challenge</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Company</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Registrations</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {enterpriseLoading ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                            <Loader className="animate-spin mx-auto mb-2" size={24} />
                            Loading...
                          </td>
                        </tr>
                      ) : enterpriseChallenges.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                            No challenges found
                          </td>
                        </tr>
                      ) : (
                        enterpriseChallenges.map((challenge) => (
                          <tr key={challenge.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div className="font-medium text-gray-900">{challenge.title}</div>
                              <div className="text-xs text-gray-500">/enterprise/{challenge.org_slug}/{challenge.slug}</div>
                            </td>
                            <td className="px-4 py-4 text-gray-700">{challenge.company_name}</td>
                            <td className="px-4 py-4">
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                {challenge.plan_details?.name || challenge.pricing_plan || 'Starter'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-gray-700">{challenge.registration_count || 0}</td>
                            <td className="px-4 py-4">
                              {/* Status Dropdown for Super Admin */}
                              <select
                                value={challenge.status}
                                onChange={(e) => initiateStatusChange(challenge, e.target.value)}
                                className={`px-2 py-1 rounded-lg text-xs font-medium border-0 cursor-pointer ${
                                  ENTERPRISE_STATUSES.find(s => s.value === challenge.status)?.color || 'bg-gray-100'
                                }`}
                                data-testid={`status-dropdown-${challenge.id}`}
                              >
                                {ENTERPRISE_STATUSES.map(status => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Quick Approve/Reject for pending */}
                                {challenge.status === 'pending_approval' && (
                                  <>
                                    <button
                                      onClick={() => handleApproveEnterprise(challenge.id)}
                                      className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                                      data-testid={`approve-btn-${challenge.id}`}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleRejectEnterprise(challenge.id)}
                                      className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700"
                                      data-testid={`reject-btn-${challenge.id}`}
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {/* Copy Links for approved/live challenges */}
                                {['approved', 'live'].includes(challenge.status) && (
                                  <>
                                    <button
                                      onClick={() => copyEnterpriseLink(challenge, 'landing')}
                                      className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded-lg hover:bg-cyan-200 flex items-center gap-1"
                                      title="Copy Challenge Link"
                                      data-testid={`copy-landing-${challenge.id}`}
                                    >
                                      <Copy size={12} /> Link
                                    </button>
                                    <button
                                      onClick={() => copyEnterpriseLink(challenge, 'assessment')}
                                      className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-lg hover:bg-purple-200 flex items-center gap-1"
                                      title="Copy Assessment Link"
                                      data-testid={`copy-assessment-${challenge.id}`}
                                    >
                                      <Copy size={12} /> Test
                                    </button>
                                    <a
                                      href={`/enterprise/${challenge.org_slug}/${challenge.slug}?ref=admin_dashboard`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 flex items-center gap-1"
                                      data-testid={`view-page-${challenge.id}`}
                                    >
                                      <Eye size={12} /> View
                                    </a>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pricing Configuration */}
            {enterpriseActiveSubTab === 'pricing' && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="text-green-500" size={20} />
                  Pricing Plans (Click to Edit)
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(enterprisePricing).map(([planId, plan]) => (
                    <div 
                      key={planId} 
                      className="border border-gray-200 rounded-xl p-4 hover:border-purple-400 hover:shadow-md cursor-pointer transition-all"
                      onClick={() => openEditEnterprisePlan(planId)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-800">{plan.name}</h4>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${plan.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {plan.enabled ? 'Active' : 'Disabled'}
                          </span>
                          <Edit className="text-gray-400" size={14} />
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-3">
                        ₹{plan.price?.toLocaleString()}
                      </div>
                      <ul className="text-sm text-gray-500 space-y-1">
                        {plan.features?.slice(0, 4).map((f, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                            {f}
                          </li>
                        ))}
                        {plan.features?.length > 4 && (
                          <li className="text-gray-400">+{plan.features.length - 4} more features</li>
                        )}
                      </ul>
                      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                        Max: {plan.max_participants?.toLocaleString() || '∞'} participants, {plan.max_rounds || '∞'} rounds
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Coupons List */}
            {enterpriseActiveSubTab === 'coupons' && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Discount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plans</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Usage</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Valid Until</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {enterpriseCoupons.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            No coupons created yet. Click "Create Coupon" to add one.
                          </td>
                        </tr>
                      ) : (
                        enterpriseCoupons.map((coupon) => (
                          <tr key={coupon.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <span className="font-mono font-semibold text-purple-600">{coupon.code}</span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="font-medium">
                                {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap gap-1">
                                {coupon.applicable_plans?.map(plan => (
                                  <span key={plan} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs capitalize">
                                    {plan}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-gray-700">
                              {coupon.used_count || 0} / {coupon.max_uses || '∞'}
                            </td>
                            <td className="px-4 py-4 text-gray-700 text-sm">
                              {coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString() : 'No expiry'}
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {coupon.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEditEnterpriseCoupon(coupon)}
                                  className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                  title="Edit"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => toggleEnterpriseCouponStatus(coupon.id)}
                                  className={`p-1.5 rounded ${coupon.is_active ? 'text-amber-500 hover:bg-amber-50' : 'text-green-500 hover:bg-green-50'}`}
                                  title={coupon.is_active ? 'Deactivate' : 'Activate'}
                                >
                                  {coupon.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                                </button>
                                <button
                                  onClick={() => deleteEnterpriseCoupon(coupon.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enterprise Pricing Edit Modal */}
        {showEnterprisePricingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">
                  Edit {enterprisePlanForm.name} Plan
                </h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                  <input
                    type="text"
                    value={enterprisePlanForm.name}
                    onChange={(e) => setEnterprisePlanForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={enterprisePlanForm.price}
                    onChange={(e) => setEnterprisePlanForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                    <input
                      type="number"
                      value={enterprisePlanForm.max_participants}
                      onChange={(e) => setEnterprisePlanForm(f => ({ ...f, max_participants: parseInt(e.target.value) || 1000 }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Rounds</label>
                    <input
                      type="number"
                      value={enterprisePlanForm.max_rounds}
                      onChange={(e) => setEnterprisePlanForm(f => ({ ...f, max_rounds: parseInt(e.target.value) || 3 }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                  <textarea
                    value={enterprisePlanForm.features?.join('\n') || ''}
                    onChange={(e) => setEnterprisePlanForm(f => ({ ...f, features: e.target.value.split('\n').filter(Boolean) }))}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                  />
                </div>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enterprisePlanForm.enabled}
                    onChange={(e) => setEnterprisePlanForm(f => ({ ...f, enabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-gray-700">Plan is active and visible to customers</span>
                </label>
              </div>
              
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => { setShowEnterprisePricingModal(false); setEditingEnterprisePlan(null); }}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEnterprisePlan}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise Coupon Modal */}
        {showEnterpriseCouponModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingEnterpriseCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code *</label>
                  <input
                    type="text"
                    value={enterpriseCouponForm.code}
                    onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    placeholder="ENTERPRISE20"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                    <select
                      value={enterpriseCouponForm.discount_type}
                      onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, discount_type: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value {enterpriseCouponForm.discount_type === 'percentage' ? '(%)' : '(₹)'}
                    </label>
                    <input
                      type="number"
                      value={enterpriseCouponForm.discount_value}
                      onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, discount_value: parseInt(e.target.value) || 0 }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min={0}
                      max={enterpriseCouponForm.discount_type === 'percentage' ? 100 : undefined}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Plans</label>
                  <div className="flex flex-wrap gap-2">
                    {['starter', 'pro', 'enterprise'].map(plan => (
                      <label key={plan} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enterpriseCouponForm.applicable_plans?.includes(plan)}
                          onChange={(e) => {
                            const plans = enterpriseCouponForm.applicable_plans || [];
                            if (e.target.checked) {
                              setEnterpriseCouponForm(f => ({ ...f, applicable_plans: [...plans, plan] }));
                            } else {
                              setEnterpriseCouponForm(f => ({ ...f, applicable_plans: plans.filter(p => p !== plan) }));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-gray-700 capitalize">{plan}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                  <input
                    type="number"
                    value={enterpriseCouponForm.max_uses}
                    onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, max_uses: parseInt(e.target.value) || 100 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min={1}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                    <input
                      type="date"
                      value={enterpriseCouponForm.valid_from}
                      onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, valid_from: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                    <input
                      type="date"
                      value={enterpriseCouponForm.valid_until}
                      onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, valid_until: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enterpriseCouponForm.is_active}
                    onChange={(e) => setEnterpriseCouponForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-gray-700">Coupon is active</span>
                </label>
              </div>
              
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => { setShowEnterpriseCouponModal(false); setEditingEnterpriseCoupon(null); }}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEnterpriseCoupon}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  {editingEnterpriseCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resume Upgrades Tab */}
        {activeTab === 'resume-upgrades' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Total Orders</p>
                <p className="text-2xl font-bold text-gray-800">{resumeUpgrades.summary?.total_orders || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Paid</p>
                <p className="text-2xl font-bold text-blue-600">{resumeUpgrades.summary?.paid || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-yellow-500">
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{resumeUpgrades.summary?.pending || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-purple-600">₹{resumeUpgrades.summary?.total_revenue || 0}</p>
              </div>
            </div>

            {/* Filters & Actions */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <select
                    value={resumeUpgradeFilter}
                    onChange={(e) => {
                      setResumeUpgradeFilter(e.target.value);
                      setResumeUpgradePage(1);
                      fetchResumeUpgrades(e.target.value, 1);
                    }}
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500"
                  >
                    <option value="all">All Orders</option>
                    <option value="paid">Paid Only</option>
                    <option value="pending">Pending Only</option>
                  </select>
                  <button
                    onClick={() => fetchResumeUpgrades()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
                <button
                  onClick={exportResumeUpgrades}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Download size={18} />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Student</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">College</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {resumeUpgradesLoading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          <Loader className="animate-spin mx-auto mb-2" size={24} />
                          Loading...
                        </td>
                      </tr>
                    ) : resumeUpgrades.orders?.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No resume upgrade orders found
                        </td>
                      </tr>
                    ) : (
                      resumeUpgrades.orders?.map((order) => (
                        <tr key={order.order_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{order.student_name}</div>
                            <div className="text-xs text-gray-500">{order.student_id}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{order.student_email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-[150px] truncate">{order.college}</td>
                          <td className="px-4 py-3 font-semibold text-gray-900">₹{order.amount}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              order.status === 'completed' ? 'bg-green-100 text-green-700' :
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              order.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={order.status}
                              onChange={(e) => updateResumeUpgradeStatus(order.order_id, e.target.value)}
                              className="px-2 py-1 text-sm border rounded-lg focus:border-orange-500"
                            >
                              <option value="pending">Pending</option>
                              <option value="completed">Completed</option>
                              <option value="failed">Failed</option>
                              <option value="refunded">Refunded</option>
                            </select>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {resumeUpgrades.pages > 1 && (
                <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Page {resumeUpgrades.page} of {resumeUpgrades.pages} ({resumeUpgrades.total} total)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const newPage = Math.max(1, resumeUpgradePage - 1);
                        setResumeUpgradePage(newPage);
                        fetchResumeUpgrades(resumeUpgradeFilter, newPage);
                      }}
                      disabled={resumeUpgradePage <= 1}
                      className="px-3 py-1 border rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const newPage = Math.min(resumeUpgrades.pages, resumeUpgradePage + 1);
                        setResumeUpgradePage(newPage);
                        fetchResumeUpgrades(resumeUpgradeFilter, newPage);
                      }}
                      disabled={resumeUpgradePage >= resumeUpgrades.pages}
                      className="px-3 py-1 border rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Interview Credits Tab */}
        {activeTab === 'interview-credits' && (
          <div className="space-y-6">
            {/* Grant Credits Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <MessageSquare className="text-blue-600" size={20} />
                Grant Athena AI Interview Credits
              </h3>
              
              {/* Search Students */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">Search Student (Name, Email, or Username)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={creditSearch}
                    onChange={(e) => setCreditSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') searchStudentsForCredits(creditSearch); }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter student name, email, or username..."
                    data-testid="credit-student-search"
                  />
                  <button
                    onClick={() => searchStudentsForCredits(creditSearch)}
                    disabled={creditSearchLoading || creditSearch.length < 2}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
                    data-testid="credit-search-btn"
                  >
                    {creditSearchLoading ? <Loader className="animate-spin" size={16} /> : 'Search'}
                  </button>
                </div>
              </div>
              
              {/* Credits Amount & Reason */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Credits to Grant</label>
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                    min={1}
                    max={100}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="credit-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Reason</label>
                  <input
                    type="text"
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Reason for granting credits"
                    data-testid="credit-reason-input"
                  />
                </div>
              </div>
              
              {/* Student Results */}
              {creditStudents.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">Student</th>
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">College</th>
                        <th className="text-center p-3 text-xs font-bold uppercase text-gray-500">Current Credits</th>
                        <th className="text-center p-3 text-xs font-bold uppercase text-gray-500">Used</th>
                        <th className="text-center p-3 text-xs font-bold uppercase text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditStudents.map((student) => (
                        <tr key={student.id} className="border-b hover:bg-blue-50 transition-colors" data-testid={`credit-student-${student.id}`}>
                          <td className="p-3">
                            <p className="font-medium text-gray-800 text-sm">{student.full_name}</p>
                            <p className="text-xs text-gray-500">{student.email}</p>
                            {student.username && <p className="text-xs text-blue-600">@{student.username}</p>}
                          </td>
                          <td className="p-3 text-sm text-gray-600">{student.college || '-'}</td>
                          <td className="p-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">
                              {student.interview_credits}
                            </span>
                          </td>
                          <td className="p-3 text-center text-sm text-gray-500">{student.total_used}</td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => grantInterviewCredits(student.id, student.full_name)}
                              disabled={grantingCredit === student.id || creditAmount <= 0}
                              className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
                              data-testid={`grant-credits-${student.id}`}
                            >
                              {grantingCredit === student.id ? (
                                <Loader className="animate-spin" size={14} />
                              ) : (
                                `+ ${creditAmount} Credits`
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {creditSearch.length >= 2 && creditStudents.length === 0 && !creditSearchLoading && (
                <div className="text-center py-8 text-gray-400">
                  <Users size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No students found for "{creditSearch}"</p>
                </div>
              )}
            </div>
            
            {/* Credit Logs */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="text-gray-500" size={20} />
                Recent Credit Grants
              </h3>
              
              {creditLogsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="animate-spin text-blue-600" size={24} />
                </div>
              ) : creditLogs.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">Student</th>
                        <th className="text-center p-3 text-xs font-bold uppercase text-gray-500">Credits</th>
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">Reason</th>
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">Admin</th>
                        <th className="text-left p-3 text-xs font-bold uppercase text-gray-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditLogs.map((log, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="p-3">
                            <p className="font-medium text-gray-800 text-sm">{log.student_name}</p>
                            <p className="text-xs text-gray-500">{log.student_email}</p>
                          </td>
                          <td className="p-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                              +{log.credits_added}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-600">{log.reason}</td>
                          <td className="p-3 text-sm text-gray-500">{log.admin_username}</td>
                          <td className="p-3 text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FileText size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No credit grants yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Future Skills Tab */}
        {activeTab === 'future-skills' && (
          <FutureSkillsAdmin token={localStorage.getItem('admin_token')} />
        )}

        {/* Colleges Management Tab */}
        {activeTab === 'colleges' && (
          <CollegesAdmin token={localStorage.getItem('admin_token')} />
        )}

        {/* Coupon Modal */}
        {showCouponModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-2xl">
                <h2 className="text-xl font-bold">{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
                <p className="text-purple-200 text-sm">Set up discount codes for your participants</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Coupon Code *</label>
                  <input
                    type="text"
                    value={couponForm.code}
                    onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SAVE20"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 font-mono"
                    maxLength={20}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Type</label>
                    <select
                      value={couponForm.discount_type}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="flat">Flat Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Value</label>
                    <input
                      type="number"
                      value={couponForm.discount_value}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Applicable To</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['hackathon_entry_fee', 'accommodation_food', 'accommodation_only', 'food_only'].map((type) => (
                      <label key={type} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={couponForm.applicable_to.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCouponForm({ ...couponForm, applicable_to: [...couponForm.applicable_to, type] });
                            } else {
                              setCouponForm({ ...couponForm, applicable_to: couponForm.applicable_to.filter(t => t !== type) });
                            }
                          }}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm">{type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Max Total Uses</label>
                    <input
                      type="number"
                      value={couponForm.max_uses || ''}
                      onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="Unlimited"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Max Uses Per User</label>
                    <input
                      type="number"
                      value={couponForm.max_uses_per_user}
                      onChange={(e) => setCouponForm({ ...couponForm, max_uses_per_user: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                      min="1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Valid From</label>
                    <input
                      type="datetime-local"
                      value={couponForm.valid_from}
                      onChange={(e) => setCouponForm({ ...couponForm, valid_from: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Valid Until</label>
                    <input
                      type="datetime-local"
                      value={couponForm.valid_until}
                      onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={couponForm.description}
                    onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                    placeholder="e.g., Early bird discount"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={couponForm.is_active}
                    onChange={(e) => setCouponForm({ ...couponForm, is_active: e.target.checked })}
                    className="w-5 h-5 text-purple-600 rounded"
                  />
                  <span className="font-semibold text-gray-700">Active</span>
                </label>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCouponModal(false);
                      setEditingCoupon(null);
                      resetCouponForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCoupon}
                    disabled={savingCoupon}
                    className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
                  >
                    {savingCoupon ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
        {/* End Content div */}

        {/* Partial Payment Modal */}
        {showPartialPaymentModal && selectedTeamForPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[95vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 sm:p-6 rounded-t-2xl sticky top-0 z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold">Partial Payment</h2>
                    <p className="text-orange-100 mt-1 text-sm sm:text-base truncate max-w-[200px] sm:max-w-none">{selectedTeamForPayment.team_name}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPartialPaymentModal(false);
                      setSelectedTeamForPayment(null);
                    }}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Team Summary */}
                <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div>
                      <span className="text-gray-500">Total Members</span>
                      <p className="font-bold text-gray-800 text-base sm:text-lg">{selectedTeamForPayment.total_members}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Amount</span>
                      <p className="font-bold text-gray-800 text-base sm:text-lg">₹{selectedTeamForPayment.total_price?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Price Per Person</span>
                      <p className="font-semibold text-gray-700">₹{selectedTeamForPayment.price_per_person?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Current Status</span>
                      <p className={`font-semibold ${
                        selectedTeamForPayment.payment_status === 'completed' ? 'text-green-600' :
                        selectedTeamForPayment.payment_status === 'partial' ? 'text-orange-600' : 'text-yellow-600'
                      }`}>
                        {selectedTeamForPayment.payment_status?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Partial Payment Form */}
                <div className="space-y-4">
                  {/* Paid Members */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Members Paid For *
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        max={selectedTeamForPayment.total_members}
                        value={partialPaymentData.paid_members}
                        onChange={(e) => {
                          const paidMembers = Math.min(Math.max(1, parseInt(e.target.value) || 1), selectedTeamForPayment.total_members);
                          setPartialPaymentData({
                            ...partialPaymentData,
                            paid_members: paidMembers,
                            amount_paid: paidMembers * selectedTeamForPayment.price_per_person
                          });
                        }}
                        className="w-24 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 text-center font-bold text-lg"
                      />
                      <span className="text-gray-500">of {selectedTeamForPayment.total_members} members</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Teams can pay for remaining members later
                    </p>
                  </div>

                  {/* Amount Paid */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Amount Paid (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={partialPaymentData.amount_paid}
                      onChange={(e) => setPartialPaymentData({
                        ...partialPaymentData,
                        amount_paid: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 font-semibold"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-calculated: {partialPaymentData.paid_members} × ₹{selectedTeamForPayment.price_per_person} = ₹{(partialPaymentData.paid_members * selectedTeamForPayment.price_per_person).toLocaleString()}
                    </p>
                  </div>

                  {/* Coupon Code */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Coupon Code Applied (Optional)
                    </label>
                    <input
                      type="text"
                      value={partialPaymentData.coupon_code}
                      onChange={(e) => setPartialPaymentData({
                        ...partialPaymentData,
                        coupon_code: e.target.value.toUpperCase()
                      })}
                      placeholder="Enter coupon code"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 font-mono"
                    />
                  </div>

                  {/* Payment Summary */}
                  <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
                    <h4 className="font-bold text-orange-800 mb-3">Payment Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Members Paid</span>
                        <span className="font-semibold text-gray-800">{partialPaymentData.paid_members} of {selectedTeamForPayment.total_members}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount Paid</span>
                        <span className="font-semibold text-green-600">₹{partialPaymentData.amount_paid?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between border-t border-orange-200 pt-2">
                        <span className="text-gray-600">Remaining Balance</span>
                        <span className="font-bold text-red-600">
                          ₹{(selectedTeamForPayment.total_price - partialPaymentData.amount_paid).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowPartialPaymentModal(false);
                      setSelectedTeamForPayment(null);
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePartialPaymentSubmit}
                    className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Save Partial Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Details Modal */}
        {showTeamDetailsModal && selectedTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 sm:p-6 rounded-t-2xl sticky top-0 z-10">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2 truncate">{selectedTeam.team_name}</h2>
                    <p className="text-blue-100 text-sm sm:text-base">Registration #{selectedTeam.registration_number}</p>
                  </div>
                  <button
                    onClick={closeTeamDetailsModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors ml-2 flex-shrink-0"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                {/* Team Overview */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border-2 border-blue-200">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    <Users className="text-blue-600" size={20} />
                    Team Overview
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">Team Leader</p>
                      <p className="text-sm sm:text-lg font-semibold text-gray-800">{selectedTeam.leader_username}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Members</p>
                      <p className="text-sm sm:text-lg font-semibold text-gray-800">{selectedTeam.total_members}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">Payment Status</p>
                      <span className={`inline-block px-2 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold ${
                        selectedTeam.payment_status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : selectedTeam.payment_status === 'partial'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedTeam.payment_status?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Fee</p>
                      <p className="text-lg font-semibold text-blue-600">₹{selectedTeam.total_price?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Amount Paid</p>
                      <p className="text-lg font-semibold text-green-600">₹{(selectedTeam.amount_paid || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Balance Due</p>
                      <p className={`text-lg font-semibold ${selectedTeam.amount_due > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        ₹{(selectedTeam.amount_due || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Members Paid</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {selectedTeam.paid_member_count || 0} / {selectedTeam.total_members}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Coupon Applied</p>
                      {selectedTeam.coupon_applied ? (
                        <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                          {selectedTeam.coupon_applied}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">None</span>
                      )}
                    </div>
                    {selectedTeam.hackathon_theme && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600 mb-1">Hackathon Theme</p>
                        <p className="text-lg font-semibold text-gray-800">{selectedTeam.hackathon_theme}</p>
                      </div>
                    )}
                    {selectedTeam.idea_description && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600 mb-1">Idea Description</p>
                        <p className="text-base text-gray-700 bg-white p-3 rounded-lg">{selectedTeam.idea_description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Team Members */}
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Users className="text-blue-600" size={24} />
                    Team Members ({selectedTeam.members?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedTeam.members?.map((member, index) => (
                      <div
                        key={index}
                        className={`bg-white border-2 rounded-xl p-5 hover:shadow-lg transition-all ${
                          index === 0 ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                                index === 0 ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}>
                                {member.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-lg font-bold text-gray-800">{member.name}</p>
                                {index === 0 && (
                                  <span className="inline-block bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-semibold">
                                    Team Leader
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-gray-600">Email</p>
                                <p className="font-semibold text-gray-800">{member.email}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Phone</p>
                                <p className="font-semibold text-gray-800">{member.phone}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">College</p>
                                <p className="font-semibold text-gray-800">{member.college}</p>
                              </div>
                              {member.workshop_choice && (
                                <div>
                                  <p className="text-gray-600">Workshop Choice</p>
                                  <p className="font-semibold text-gray-800">{member.workshop_choice}</p>
                                </div>
                              )}
                              {member.mode && (
                                <div>
                                  <p className="text-gray-600">Participation Mode</p>
                                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                    member.mode === 'Online' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {member.mode}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <DollarSign className="text-green-600" size={24} />
                    Payment Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Paid Members</p>
                      <p className="text-lg font-semibold text-gray-800">{selectedTeam.paid_member_count} / {selectedTeam.total_members}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                      <p className="text-2xl font-bold text-green-600">₹{selectedTeam.total_price}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end gap-3 border-t-2 border-gray-200">
                <button
                  onClick={closeTeamDetailsModal}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Admin Modal */}
        {showAddAdminModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UserCog size={28} />
                    <div>
                      <h2 className="text-xl font-bold">Add Admin User</h2>
                      <p className="text-cyan-100 text-sm">Create a new admin account</p>
                    </div>
                  </div>
                  <button onClick={() => setShowAddAdminModal(false)} className="text-white/80 hover:text-white">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Username *</label>
                  <input
                    type="text"
                    value={newAdminForm.username}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, username: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:border-cyan-500"
                    placeholder="Enter username"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={newAdminForm.name}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:border-cyan-500"
                    placeholder="Enter full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    value={newAdminForm.password}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:border-cyan-500"
                    placeholder="Min 8 characters"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Role *</label>
                  <select
                    value={newAdminForm.role}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, role: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:border-cyan-500"
                  >
                    <option value="view_only">View Only</option>
                    <option value="edit_only">Edit Only</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddAdminModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createAdminUser}
                    disabled={creatingAdmin}
                    className="flex-1 bg-cyan-600 text-white py-3 rounded-lg font-semibold hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {creatingAdmin ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Create Admin
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logout All Sessions Confirmation Modal */}
        {showLogoutAllModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-full">
                    <Shield size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Security Action</h2>
                    <p className="text-amber-100 text-sm">Logout from all devices</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
                    <div>
                      <p className="text-amber-800 font-semibold mb-1">Are you sure?</p>
                      <p className="text-amber-700 text-sm">
                        This will immediately log you out from <strong>all devices and browsers</strong> where you&apos;re currently signed in. You&apos;ll need to log in again on each device.
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-6">
                  Use this if you suspect unauthorized access to your admin account or if you&apos;ve logged in on a shared/public device.
                </p>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLogoutAllModal(false)}
                    disabled={loggingOutAll}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={logoutAllSessions}
                    disabled={loggingOutAll}
                    className="flex-1 bg-amber-500 text-white py-3 rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loggingOutAll ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Logging out...
                      </>
                    ) : (
                      <>
                        <Shield size={18} />
                        Logout All Sessions
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise Status Change Confirmation Modal */}
        {showStatusConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl" data-testid="status-confirm-modal">
              <div className={`p-6 text-white ${
                statusChangeData.newStatus === 'live' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                statusChangeData.newStatus === 'expired' ? 'bg-gradient-to-r from-gray-500 to-gray-600' :
                statusChangeData.newStatus === 'cancelled' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                'bg-gradient-to-r from-amber-500 to-amber-600'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-full">
                    <AlertCircle size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Confirm Status Change</h2>
                    <p className="text-white/80 text-sm">This is a critical action</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Challenge:</p>
                  <p className="font-semibold text-gray-900">{statusChangeData.challengeTitle}</p>
                </div>
                
                <div className="flex items-center justify-center gap-3">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    {statusChangeData.currentStatus}
                  </span>
                  <ChevronRight className="text-gray-400" size={20} />
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    statusChangeData.newStatus === 'live' ? 'bg-green-100 text-green-700' :
                    statusChangeData.newStatus === 'expired' ? 'bg-gray-200 text-gray-700' :
                    statusChangeData.newStatus === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {statusChangeData.newStatus}
                  </span>
                </div>
                
                {['rejected', 'cancelled'].includes(statusChangeData.newStatus) && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Reason (optional)</label>
                    <textarea
                      value={statusChangeData.reason}
                      onChange={(e) => setStatusChangeData({ ...statusChangeData, reason: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:border-purple-500 resize-none"
                      rows={3}
                      placeholder="Enter reason for this status change..."
                      data-testid="status-reason-input"
                    />
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    {statusChangeData.newStatus === 'live' && 'Going live will make this challenge visible to participants.'}
                    {statusChangeData.newStatus === 'expired' && 'Expiring will close registrations and mark the challenge as ended.'}
                    {statusChangeData.newStatus === 'cancelled' && 'Cancelling will end the challenge and notify registered participants.'}
                    {statusChangeData.newStatus === 'rejected' && 'Rejecting will notify the company and prevent the challenge from going live.'}
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowStatusConfirmModal(false);
                      setStatusChangeData({ challengeId: '', challengeTitle: '', currentStatus: '', newStatus: '', reason: '' });
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                    data-testid="status-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdateEnterpriseStatus(statusChangeData.challengeId, statusChangeData.newStatus, statusChangeData.reason)}
                    disabled={updatingStatus}
                    className={`flex-1 text-white py-3 rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${
                      statusChangeData.newStatus === 'live' ? 'bg-green-600 hover:bg-green-700' :
                      statusChangeData.newStatus === 'cancelled' || statusChangeData.newStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-purple-600 hover:bg-purple-700'
                    }`}
                    data-testid="status-confirm-btn"
                  >
                    {updatingStatus ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Confirm Change
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;